/**
 * StrategiesSection.js - Support at Home "Optimise Your Plan" / strategies tab.
 * Manual and suggestion strategies, projections modal, aged care integration.
 */
import { anyoneOwnsHome } from '../../../../shared/src/calculation/householdPosition.ts';
import React, { useState, useEffect, useMemo } from 'react';
import {
    pensionClause,
    cashflowClause,
    estateClause,
    plainClause,
    RecapClauses,
    type RecapClause,
} from '../../components/strategy/recapClauses.tsx';
import { Home, ShieldCheck, Info, TrendingUp, TrendingUp as TrendingUpIcon, TrendingDown, Users, Gift, Eye, Wrench, AlertCircle } from 'lucide-react';
import { getAgedCareModellerResidencyOverrides, isIllnessSeparatedCouple } from '../../../../shared/src/calculation/householdStatus.ts';
import { buildNetWorthSchedule } from '../../../../shared/src/calculation/netWorthSchedule.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { extractAgedCareIntegrationDetails, applyRadFundingToFactFind } from '../../strategy/StrategyIntegrationUtils.ts';
import { buildAnnuityImpact, summariseAnnuityImpact, applyAnnuityToFactFind } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { getServicePlanFromStorage, getServicePlanWithSource, listSavedServicePlans, setCurrentServicePlan, clearCurrentServicePlan, usableSavedServicePlans, type ServicePlanSource } from '../../../ServicePlanUtils.ts';
import { calculateAge } from '../../utils/index.ts';
import { CurrencyInput, applyDownsizeToFactFind, applyRenovateToFactFind, applyUpgradeToFactFind } from './SupportAtHomeHelpers.tsx';
import {
    SupportDownsizeStrategyAllocator,
    ReverseMortgageStrategyAllocator,
    HEASStrategyAllocator,
    RenovateStrategyAllocator,
    UpgradeStrategyAllocator,
} from './SupportAtHomePropertyAllocators.tsx';
import { CalculationTooltip, GradientText, Button, Badge, ImpactMetric } from '../../components/ui/index.tsx';
import { useDoNothingLegacy } from '../../utils/doNothingBaseline.ts';
import type { BreakdownRow } from './supportAtHomePensionInsightUtils.tsx';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from '../agedCare/projectionsModalPreferences.ts';
import {
    buildSupportAtHomeDigitalSOASnapshot,
    openSupportAtHomeDigitalSOAAttachment,
} from './SupportAtHomeDigitalSOAAttachment.tsx';
import DebtPayoffStrategy from '../agedCare/DebtPayoffStrategy.tsx';
import type { DebtPayoff } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import SupportAtHomeAnnuityConfigurator, { DEFAULT_NEW_ANNUITY_RESIDUAL } from './SupportAtHomeAnnuityConfigurator.tsx';
import type { AnnuityDetails, AnnuityFundingSource } from './SupportAtHomeAnnuityConfigurator.tsx';
import SupportAtHomeSuggestionEnginePanel from './SupportAtHomeSuggestionEnginePanel.tsx';
import SahPlanRecap, { type SahIntegrationSummary } from './SahPlanRecap.tsx';
// The SHARED panel and impact card — not module copies. Support at Home had hand-rolled its own
// Financial Impact block, which is why every improvement made to the shared one stopped at the
// Aged Care module (owner, 2026-08-27: make the modules consistent).
import PlanSummaryPanel from '../agedCare/PlanSummaryPanel.tsx';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import {
    GiftingFieldNote,
    ReverseMortgageFieldNote,
    HeasFieldNote,
} from '../../components/strategy/StrategyFieldNotes.tsx';
import FinancialImpactPanel from '../agedCare/FinancialImpactPanel.tsx';
import SahStartingPosition from './SahStartingPosition.tsx';
import { simulateManualSahPlan } from './sahPlanFactFind.ts';
import { computeSahDownsizeUnallocated } from './SupportAtHomePropertyAllocators.tsx';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { describeServicePlan, servicePlanBasisWarning, servicePlanDistribution, type ServicePlanBasis } from './servicePlanBasis.ts';
import { FUNERAL_BOND_LIMIT } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, PlainWordsBeat } from '../../components/narrative/PlainWords.tsx';
import { ModeChooser } from '../../components/ui/index.tsx';
import type { SupportAtHomeServicePlan } from '../../strategy/supportAtHomeEstimatedServicePlan.ts';
import { resolveServicePlanForSuggestionEngine, hasUsableServicePlan } from '../../strategy/supportAtHomeEstimatedServicePlan.ts';
import { SUPPORT_AT_HOME_SERVICES } from './supportAtHomeServices.tsx';
import { computeServiceCostsForPerson } from './serviceBuilderCosts.ts';
import {
    getSavedHomeCareStrategies,
    persistHomeCareStrategy,
    HOME_CARE_STRATEGIES_UPDATED_EVENT,
} from './homeCareStrategyStorage.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import type {
    SupportDownsizeAllocatorDetails,
    ReverseMortgageAllocatorDetails,
    HeasAllocatorDetails,
} from './SupportAtHomePropertyAllocators.tsx';
import type { RenovateDetails, UpgradeDetails } from './SupportAtHomeHelpers.tsx';
import type { DownsizeDetails as StrategyDownsizeDetails } from '../../strategy/supportAtHomeStrategyFunding.ts';

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';
/** Manual Control's user-entered gifting/funeral-bond strategy state. */
interface ManualStrategiesDraft {
    gifting: number;
    funeralBonds: number;
    giftingSource: string;
    funeralSource: string;
    [key: string]: unknown;
}

/** Manual Control's overall strategy toggles (reverse mortgage / HEAS are mutually exclusive with each other). */
interface OverallStrategyDraft {
    reverseMortgage?: boolean;
    heas?: boolean;
    [key: string]: unknown;
}

/** Draft annuity configuration edited via SupportAtHomeAnnuityConfigurator, before buildAnnuityImpact resolves it.
 * `age` (the client's age) isn't part of AnnuityConfigurator's own AnnuityDetails shape (which only tracks
 * `partnerAge`) but is needed here to call the calc engine's buildAnnuityImpact. */
type AnnuityRecordDraft = AnnuityDetails & { age?: number | null };

/** Calc-engine `financials` result bag — only the fields this component reads off it are named here. */
interface SahFinancialsSnapshot {
    assessableAssets?: number;
    totalAssessableIncome?: number;
    socialSecurity?: number;
    socialSecurityBreakdown?: BreakdownRow[];
    cashFlow?: number;
    cashFlowBreakdown?: BreakdownRow[];
    netWorth?: number;
    [key: string]: unknown;
}

interface SahContributionRateSet {
    clinical?: number;
    independence?: number;
    everydayLiving?: number;
    [key: string]: unknown;
}

interface SahContributionBranch {
    contributionRates?: SahContributionRateSet;
    contributionAmounts?: { totalAnnual?: number };
    budget?: { total?: number };
    [key: string]: unknown;
}

interface SahContributionsResult {
    client?: SahContributionBranch;
    combined?: SahContributionBranch;
    partner?: SahContributionBranch;
    [key: string]: unknown;
}

/** One year of a projection (runProjections result row). */
interface SahProjectionYear {
    year?: number;
    cashFlow?: number;
    netWorth?: number;
    fyFullYearView?: unknown;
    [key: string]: unknown;
}

interface CombinedCareCosts {
    totalDailyCost: number;
    supportAtHomeDailyCost: number;
    agedCareDailyCost: number;
    supportAtHomeAnnualCost: number;
    agedCareAnnualCost: number;
    [key: string]: unknown;
}

/** factFind.servicePlan is a free-form bag (FactFind's `servicePlan?: Record<string, unknown>`) —
 * this narrower local shape covers the specific fields this component reads off it. */
interface SahServicePlanShape {
    serviceDistribution?: { clinical?: number; independence?: number; everyday?: number };
    services?: Record<string, unknown>;
    [key: string]: unknown;
}

/** Inputs bag of a previously-saved home_care strategy, as restored from sessionStorage to reopen
 * "Optimise Your Plan" pre-populated. Superset of SavedStrategy's free-form `inputs` (StrategyIntegrationUtils.ts). */
interface RestoredHomeCareStrategyInputs {
    gifting?: number | string;
    funeralBonds?: number | string;
    giftingSource?: string;
    funeralSource?: string;
    propertyStrategy?: string;
    annuityRecord?: AnnuityRecordDraft | null;
    overallStrategy?: OverallStrategyDraft;
    downsizeDetails?: SupportDownsizeAllocatorDetails;
    renovateDetails?: RenovateDetails;
    upgradeDetails?: UpgradeDetails;
    reverseMortgageDetails?: ReverseMortgageAllocatorDetails;
    heasDetails?: HeasAllocatorDetails;
    selectedAgedCareStrategyId?: string | null;
    selectedFundingStrategy?: string | null;
    [key: string]: unknown;
}

interface RestoredHomeCareStrategy {
    id?: string;
    inputs?: RestoredHomeCareStrategyInputs;
    [key: string]: unknown;
}

export interface StrategiesSectionProps {
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    onNext: () => void;
    onBack: () => void;
    strategyMode?: string;
    setStrategyMode?: (mode: string) => void;
    onNavigateToBuilder?: () => void;
    onNavigateToDashboard?: () => void;
    /**
     * When support services begin, owned by the page and SET in the Service Builder.
     *
     * A property of the service plan, not of the optimisation — but it is the date the Suggestion
     * Engine projects every candidate from, so this screen reads it. Lifted rather than duplicated:
     * two inputs for one fact is how a screen comes to disagree with the screen before it.
     */
    supportStartDate?: string;
    /**
     * Called with each generated Digital SOA (the strategy plus its printable snapshot) so it lands on
     * the server's advice register like aged care's (owner, 1 Oct 2026). App.tsx decides where.
     */
    onSoaGenerated?: (document: Record<string, unknown>) => void;
}

/** Picker value for builder selections that were never saved as a named plan. */
const UNSAVED_PLAN_OPTION = '__unsaved__';

const StrategiesSection = ({ factFind, setFactFind, strategyMode = 'manual', setStrategyMode, onNavigateToBuilder, onNavigateToDashboard, supportStartDate = '', onSoaGenerated }: StrategiesSectionProps) => {
    // Calculate participant type and other variables needed for contribution calculations
    const isPartnered = factFind?.adviceFor === 'couple';
    const defaultParticipantType = factFind?.careNeeds?.clientAcat?.status === 'yes' && factFind?.careNeeds?.clientAcat?.homeCareLevel
        ? (isPartnered ? 'couple' : 'single')
        : (isPartnered ? 'couple_partner' : 'single');
    const defaultNwopCategory = factFind?.careNeeds?.clientAcat?.nwopCategory || 'standard';
    const defaultPreviousITCF = factFind?.careNeeds?.clientAcat?.previousITCF || 0;
    
    const [participantType] = useState(defaultParticipantType);
    const [nwopCategory] = useState(defaultNwopCategory);
    const [meansDisclosed] = useState(true);
    const [previousITCF] = useState(defaultPreviousITCF);
    
    // Read restored strategy once at the top level (before useState calls)
    // Only restore if explicitly set (from Dashboard reopen), not from leftover sessionStorage
    const restoredStrategy: RestoredHomeCareStrategy | null = (() => {
        try {
            const stored = typeof window !== 'undefined' ? sessionStorage.getItem('careCompanionRestoreHomeCareStrategy') : null;
            if (stored) {
                const strategy = JSON.parse(stored);
                // Only restore if this is a saved strategy (has an id), not just leftover state
                // This prevents restoring unsaved strategies from previous sessions
                if (strategy.id) {
                    sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
                    return strategy;
                } else {
                    // Clear leftover unsaved strategy data
                    sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
                }
            }
        } catch (error) {
            console.error('Error restoring home care strategy:', error);
            // Clear corrupted data
            if (typeof window !== 'undefined') {
                sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
            }
        }
        return null;
    })();
    
    // Use strategyMode from props if provided, otherwise use internal state
    // '' = not chosen yet: only the chooser shows. A strategy reopened from the dashboard goes
    // straight to Manual Control (its levers are what was reopened).
    const [internalStrategyMode, setInternalStrategyMode] = useState<string>(strategyMode || (restoredStrategy ? 'manual' : ''));
    /** Which Support at Home suggestion-engine card is visually selected (matches Aged Care behaviour). */
    const [sahSeSelectedStrategy, setSahSeSelectedStrategy] = useState<Record<string, unknown> | null>(null);
    /** A just-saved manual strategy plus its projections as they stood at save time — the offer to
     * generate its Digital SOA (the save reset wipes the live state, so this is captured first). */
    const [manualSoaOffer, setManualSoaOffer] = useState<{ strategy: Record<string, unknown>; projections: Record<string, unknown>[] | null } | null>(null);

    const openManualDigitalSoa = async () => {
        if (!manualSoaOffer) return;
        const snapshot = await buildSupportAtHomeDigitalSOASnapshot({
            selectedStrategy: manualSoaOffer.strategy as never,
            factFind,
            projections: manualSoaOffer.projections as never,
            analysis: null,
            servicePlan: getServicePlanFromStorage() as never,
            strategySource: 'manual_control',
        });
        if (snapshot) {
            const document = { ...manualSoaOffer.strategy, soaSnapshot: snapshot };
            persistHomeCareStrategy(document as never);
            onSoaGenerated?.(document as Record<string, unknown>);
            openSupportAtHomeDigitalSOAAttachment(snapshot);
        }
        setManualSoaOffer(null);
    };
    /** When true in suggestion mode, unblur the right column and show fact-find baseline only (AC parity). */
    const [showBaseline, setShowBaseline] = useState(false);
    const [sahSeAnalyzing, setSahSeAnalyzing] = useState(false);
    const [savedHomeCareStrategies, setSavedHomeCareStrategies] = useState<SavedStrategy[]>(() => getSavedHomeCareStrategies());

    // Update internal state when prop changes
    useEffect(() => {
        if (strategyMode !== undefined) {
            setInternalStrategyMode(strategyMode);
        }
    }, [strategyMode]);

    // Update parent when internal state changes (if setStrategyMode prop provided)
    const handleStrategyModeChange = (mode: string) => {
        setInternalStrategyMode(mode);
        if (mode === 'manual') {
            setSahSeSelectedStrategy(null);
            setShowBaseline(false);
            setSahSeAnalyzing(false);
        }
        if (setStrategyMode) {
            setStrategyMode(mode);
        }
    };

    useEffect(() => {
        const refreshSavedStrategies = () => setSavedHomeCareStrategies(getSavedHomeCareStrategies());
        refreshSavedStrategies();
        if (typeof window !== 'undefined') {
            window.addEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refreshSavedStrategies);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refreshSavedStrategies);
            }
        };
    }, []);
    
    const [manualStrategies, setManualStrategies] = useState<ManualStrategiesDraft>(() => {
        // Restore manual strategies from saved strategy
        if (restoredStrategy?.inputs) {
            return {
                gifting: Number(restoredStrategy.inputs.gifting) || 0,
                funeralBonds: Number(restoredStrategy.inputs.funeralBonds) || 0,
                giftingSource: restoredStrategy.inputs.giftingSource || 'bank',
                funeralSource: restoredStrategy.inputs.funeralSource || 'bank'
            };
        }
        return {
            gifting: 0,
            funeralBonds: 0,
            giftingSource: 'bank',
            funeralSource: 'bank'
        };
    });
    const [propertyStrategy, setPropertyStrategy] = useState<string>(restoredStrategy?.inputs?.propertyStrategy || 'none'); // 'none', 'downsize', 'renovate', 'upgrade', 'reverseMortgage', 'heas'
    const [annuityRecord, setAnnuityRecord] = useState<AnnuityRecordDraft | null>(restoredStrategy?.inputs?.annuityRecord || null);
    const [overallStrategy, setOverallStrategy] = useState<OverallStrategyDraft>(restoredStrategy?.inputs?.overallStrategy || {});
    const [downsizeDetails, setDownsizeDetails] = useState<SupportDownsizeAllocatorDetails>(() => {
        if (restoredStrategy?.inputs?.downsizeDetails) {
            return restoredStrategy.inputs.downsizeDetails;
        }
        return {
            salePrice: factFind?.home?.value || 0,
            sellingCosts: (Number(factFind?.home?.value) || 0) * 0.025,
            newHomePurchasePrice: 0,
            downsizerContribution: 0,
            partnerDownsizerContribution: 0,
            giftingAmount: 0,
            funeralBondPurchase: 0,
            addedAssets: []
        };
    });
    const [renovateDetails, setRenovateDetails] = useState<RenovateDetails>(() => {
        return restoredStrategy?.inputs?.renovateDetails || {
            renovationAmount: 0,
            fundingSources: []
        };
    });
    const [upgradeDetails, setUpgradeDetails] = useState<UpgradeDetails>(() => {
        if (restoredStrategy?.inputs?.upgradeDetails) {
            return restoredStrategy.inputs.upgradeDetails;
        }
        return {
            currentHomeValue: parseFloat(String(factFind.home?.value)) || 0,
            newHomePurchasePrice: 0,
            sellingCosts: 0,
            fundingSources: []
        };
    });
    const [reverseMortgageDetails, setReverseMortgageDetails] = useState<ReverseMortgageAllocatorDetails>(() => {
        return restoredStrategy?.inputs?.reverseMortgageDetails || {
            loanAmount: 0,
            loanPercentage: 0,
            interestRate: REVERSE_MORTGAGE_DEFAULT_RATE,
            loanStructure: 'lump_sum',
            regularPaymentAmount: 0,
            regularPaymentFrequency: 'fortnightly',
            establishmentFees: 0
        };
    });
    const [heasDetails, setHeasDetails] = useState<HeasAllocatorDetails>(() => {
        return restoredStrategy?.inputs?.heasDetails || {
            paymentAmount: 0,
            paymentFrequency: 'fortnightly',
            interestRate: 3.95
        };
    });
    const [availableAgedCareStrategies, setAvailableAgedCareStrategies] = useState<SavedStrategy[]>([]);
    const [selectedAgedCareStrategyId, setSelectedAgedCareStrategyId] = useState<string | null>(restoredStrategy?.inputs?.selectedAgedCareStrategyId || null);
    const [selectedFundingStrategy, setSelectedFundingStrategy] = useState<string | null>(restoredStrategy?.inputs?.selectedFundingStrategy || null);
    const [legacyYears, setLegacyYears] = useState(5);
    const [showProjectionsModal, setShowProjectionsModal] = useState(false);
    /** "Understanding your plan" — the plain-words recap on its own surface, as in Aged Care. */
    const [showSahExplanation, setShowSahExplanation] = useState(false);
    const [resetTrigger, setResetTrigger] = useState(0);
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [optimiseStrategyName, setOptimiseStrategyName] = useState('');
    const [optimiseStrategyNameError, setOptimiseStrategyNameError] = useState('');

    // Load saved aged care strategies for integration
    useEffect(() => {
        const refreshStrategies = () => {
            try {
                const stored = typeof window !== 'undefined' ? sessionStorage.getItem('careCompanionStrategies') : null;
                if (!stored) {
                    setAvailableAgedCareStrategies([]);
                    return;
                }
                const parsed = JSON.parse(stored);
                const agedCareList: SavedStrategy[] = Array.isArray(parsed)
                    ? parsed.filter((strategy: SavedStrategy) => strategy?.type === 'aged_care')
                    : [];
                setAvailableAgedCareStrategies(agedCareList);
            } catch (error) {
                console.error('Error loading aged care strategies for integration:', error);
                setAvailableAgedCareStrategies([]);
            }
        };

        refreshStrategies();

        const handleStorage = (event: StorageEvent | undefined) => {
            if (!event || event.key === 'careCompanionStrategies') {
                refreshStrategies();
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('storage', handleStorage);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('storage', handleStorage);
            }
        };
    }, []);

    useEffect(() => {
        if (!selectedAgedCareStrategyId) return;
        const exists = availableAgedCareStrategies.some(strategy => strategy.id === selectedAgedCareStrategyId);
        if (!exists) {
            setSelectedAgedCareStrategyId(null);
        }
    }, [availableAgedCareStrategies, selectedAgedCareStrategyId]);

    const selectedAgedCareStrategy = useMemo(() => {
        if (!selectedAgedCareStrategyId) return null;
        return availableAgedCareStrategies.find(strategy => strategy.id === selectedAgedCareStrategyId) || null;
    }, [availableAgedCareStrategies, selectedAgedCareStrategyId]);

    const selectedAgedCareIntegration = useMemo(() => {
        return extractAgedCareIntegrationDetails(selectedAgedCareStrategy);
    }, [selectedAgedCareStrategy]);

    const agedCareResidencyOverrides = useMemo(() => {
        if (!selectedAgedCareIntegration) return {};
        const fromLinkedStrategy = selectedAgedCareIntegration?.strategyOptions || {};
        const linkedOverrides = {
            ...(fromLinkedStrategy?.clientResidencyOverride ? { clientResidencyOverride: fromLinkedStrategy.clientResidencyOverride } : {}),
            ...(fromLinkedStrategy?.partnerResidencyOverride ? { partnerResidencyOverride: fromLinkedStrategy.partnerResidencyOverride } : {}),
        };
        if (Object.keys(linkedOverrides).length > 0) return linkedOverrides;
        return getAgedCareModellerResidencyOverrides(factFind as unknown as FactFind);
    }, [selectedAgedCareIntegration, factFind]);

    /*
     * Who moves into care under the linked plan, and whether that makes the household an
     * illness-separated couple — derived from the SAME residency overrides the calculations run
     * with, so the words can never disagree with the figures (the aged care module's frame rule).
     */
    const integrationSummary = useMemo<SahIntegrationSummary | null>(() => {
        if (!selectedAgedCareIntegration) return null;
        const overrides = (selectedAgedCareIntegration?.strategyOptions || {}) as {
            clientResidencyOverride?: string;
            partnerResidencyOverride?: string;
        };
        const partnerInCare = overrides.partnerResidencyOverride === 'aged_care';
        const clientInCare = overrides.clientResidencyOverride === 'aged_care' || !partnerInCare;
        const personName =
            (clientInCare ? factFind?.client?.firstName : factFind?.partner?.firstName) ||
            (clientInCare ? 'the client' : 'their partner');
        const illnessSeparated =
            factFind?.adviceFor === 'couple' &&
            isIllnessSeparatedCouple(factFind as unknown as FactFind, agedCareResidencyOverrides as never);
        return {
            name: String(selectedAgedCareStrategy?.name || 'aged care plan'),
            personName,
            annualFees: Number(selectedAgedCareIntegration.annualCost) || 0,
            radPaid: Number(selectedAgedCareIntegration.radPaid) || 0,
            illnessSeparated,
        };
    }, [selectedAgedCareIntegration, selectedAgedCareStrategy, factFind, agedCareResidencyOverrides]);

    
    // Save selected strategy to sessionStorage so ServiceBuilderSection can access it
    useEffect(() => {
        if (selectedAgedCareStrategy && typeof window !== 'undefined') {
            sessionStorage.setItem('careCompanionSelectedAgedCareStrategy', JSON.stringify(selectedAgedCareStrategy));
        } else if (!selectedAgedCareStrategy && typeof window !== 'undefined') {
            sessionStorage.removeItem('careCompanionSelectedAgedCareStrategy');
        }
    }, [selectedAgedCareStrategy]);
    
    const hasAgedCareStrategies = availableAgedCareStrategies.length > 0;
    
    // Calculate available annuity funding sources
    const availableAnnuityFundingSources = useMemo<AnnuityFundingSource[]>(() => {
        const sources: AnnuityFundingSource[] = [];
        const formatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const clientName = factFind?.client?.firstName || 'Client';
        const partnerName = factFind?.partner?.firstName || 'Partner';

        const ownershipLabel = (ownership: string | undefined): string => {
            if (!ownership) return '';
            if (ownership === 'joint') return 'Joint';
            if (ownership === 'partner') return partnerName;
            return clientName;
        };

        const pushSource = (type: string, asset: { id?: string | number; accountId?: string | number; ownership?: string } | undefined, amount: number | string | undefined, fallbackLabel: string) => {
            const numericAmount = parseFloat(String(amount)) || 0;
            if (numericAmount <= 0) return; // Only include sources with value
            const ownership = asset?.ownership || (factFind?.adviceFor === 'couple' ? 'joint' : 'client');
            const label = `${fallbackLabel}${ownership ? ` (${ownershipLabel(ownership)})` : ''} – ${formatter.format(numericAmount)}`;
            sources.push({
                id: asset?.id ? `${type}-${asset.id}` : `${type}-${fallbackLabel}`,
                type,
                assetId: asset?.id ?? asset?.accountId ?? null,
                label,
                ownership
            });
        };

        // Use optimized factFind that includes property strategy + aged care integration changes
        let formDataForFunding: DraftFactFind = factFind;

        // Apply property strategies to factFind for funding source calculation
        if (propertyStrategy === 'downsize' && Number(downsizeDetails.salePrice) > 0) {
            const netProceeds = (parseFloat(String(downsizeDetails.salePrice)) || 0) - (parseFloat(String(downsizeDetails.sellingCosts)) || 0);
            const totalAllocated = (parseFloat(String(downsizeDetails.newHomePurchasePrice)) || 0) +
                (parseFloat(String(downsizeDetails.downsizerContribution)) || 0) +
                (parseFloat(String(downsizeDetails.partnerDownsizerContribution)) || 0) +
                (parseFloat(String(downsizeDetails.giftingAmount)) || 0) +
                (parseFloat(String(downsizeDetails.debtRepayment)) || 0);
            const unallocatedProceeds = netProceeds - totalAllocated;
            if (unallocatedProceeds > 0) {
                sources.push({
                    id: 'downsize-proceeds',
                    type: 'bank',
                    assetId: null,
                    label: `Home Sale Proceeds – ${formatter.format(unallocatedProceeds)}`,
                    ownership: factFind?.adviceFor === 'couple' ? 'joint' : 'client'
                });
            }
        }

        // Apply linked aged care strategy funding impacts (e.g. RAD payments)
        if (selectedAgedCareIntegration?.radFunding) {
            try {
                formDataForFunding = applyRadFundingToFactFind(formDataForFunding, selectedAgedCareIntegration.radFunding);
            } catch (error) {
                console.error('Error applying aged care RAD funding to annuity sources:', error);
            }
        }

        (formDataForFunding.assets?.accountBasedPensions || []).forEach(pension => {
            const amount = pension.value ?? pension.balance ?? 0;
            pushSource('abp', pension, amount, pension.description || 'Account-based pension');
        });

        (formDataForFunding.assets?.superannuation || []).forEach(superItem => {
            pushSource('super', superItem, superItem.value, superItem.description || 'Superannuation');
        });

        (formDataForFunding.assets?.shares || []).forEach(share => {
            pushSource('shares', share, share.value, share.description || 'Shares / managed fund');
        });

        (formDataForFunding.assets?.bankAccounts || []).forEach(account => {
            pushSource('bank', account, account.value, account.description || 'Bank account');
        });

        return sources;
    }, [factFind, factFind?.adviceFor, factFind?.client?.firstName, factFind?.partner?.firstName, propertyStrategy, downsizeDetails, selectedAgedCareIntegration]);
    
    // Initialize annuity record with proper structure
    const initializeAnnuityRecord = (): AnnuityRecordDraft => {
        const isCouple = factFind?.adviceFor === 'couple';
        return {
            annuityType: 'lifetime',
            purchaseAmount: 0,
            // See DEFAULT_NEW_ANNUITY_RESIDUAL — capital preserved unless the client chooses otherwise.
            residualCapital: DEFAULT_NEW_ANNUITY_RESIDUAL,
            owner: isCouple ? 'joint' : 'client',
            fundingSource: {
                id: 'bank-default',
                type: 'bank',
                assetId: null,
                label: 'Bank accounts / cash',
                ownership: isCouple ? 'joint' : 'client'
            },
            indexation: false,
            reversionary: false,
            age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
            partnerAge: isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null
        };
    };

    // Calculate annuity record for financial impact (from annuityDetails state). buildAnnuityImpact is the
    // real (strict) calc-engine function; the Draft factFind cast is safe (see factFindTypes.ts header comment).
    const annuityRecordForCalculation: Record<string, unknown> | null = useMemo(() => {
        if (!annuityRecord || !annuityRecord.purchaseAmount || Number(annuityRecord.purchaseAmount) <= 0) return null;
        try {
            return buildAnnuityImpact(factFind as unknown as FactFind, annuityRecord);
        } catch (error) {
            console.error('Error building annuity impact', error);
            return null;
        }
    }, [annuityRecord, factFind]);

    // Calculate annuity summary for display
    const annuitySummary = useMemo(() => {
        if (!annuityRecordForCalculation) return null;
        return summariseAnnuityImpact(annuityRecordForCalculation);
    }, [annuityRecordForCalculation]);

    // Calculate annuity warnings
    const annuityWarnings = useMemo<string[]>(() => {
        if (!annuityRecord || !annuityRecord.purchaseAmount || Number(annuityRecord.purchaseAmount) <= 0) return [];
        const warnings: string[] = [];
        const purchaseAmount = parseFloat(String(annuityRecord.purchaseAmount)) || 0;

        const selectedSource = availableAnnuityFundingSources.find(src => src.id === annuityRecord.fundingSource?.id);
        const sourceType = selectedSource?.type || annuityRecord.fundingSource?.type;

        const balanceForSource = (): number => {
            if (!sourceType) return Infinity;
            const matchId = selectedSource?.assetId;
            const totalOrMatch = (list: { id?: string | number; accountId?: string | number; value?: number; balance?: number }[] | undefined): number => {
                if (!Array.isArray(list)) return 0;
                if (matchId === null || matchId === undefined) {
                    return list.reduce((sum, item) => sum + (item.value ?? item.balance ?? 0), 0);
                }
                const match = list.find(item => String(item.id ?? item.accountId) === String(matchId));
                return match ? (match.value ?? match.balance ?? 0) : 0;
            };

            if (sourceType === 'bank') return totalOrMatch(factFind.assets?.bankAccounts);
            if (sourceType === 'shares') return totalOrMatch(factFind.assets?.shares);
            if (sourceType === 'abp') return totalOrMatch(factFind.assets?.accountBasedPensions);
            if (sourceType === 'super') return totalOrMatch(factFind.assets?.superannuation);
            return Infinity;
        };

        const availableBalance = balanceForSource();
        if (purchaseAmount > availableBalance) {
            warnings.push('Purchase amount exceeds the available balance in the selected funding source.');
        }

        if (!selectedSource) {
            warnings.push('Please confirm a funding source for the annuity.');
        }

        return warnings;
    }, [annuityRecord, availableAnnuityFundingSources, factFind.assets]);

    // Update annuityRecord funding source when available sources change
    useEffect(() => {
        if (!annuityRecord || availableAnnuityFundingSources.length === 0) return;

        const fallbackSource: AnnuityFundingSource = availableAnnuityFundingSources[0] || {
            id: 'bank-default',
            type: 'bank',
            assetId: null,
            label: 'Bank accounts / cash',
            ownership: factFind?.adviceFor === 'couple' ? 'joint' : 'client'
        };

        if (!annuityRecord.fundingSource || !availableAnnuityFundingSources.some(src => src.id === annuityRecord.fundingSource?.id)) {
            setAnnuityRecord(prev => (prev ? {
                ...prev,
                fundingSource: fallbackSource
            } : prev));
        }
    }, [availableAnnuityFundingSources, annuityRecord, factFind?.adviceFor]);
    
    // Update overallStrategy when propertyStrategy changes
    useEffect(() => {
        if (propertyStrategy === 'reverseMortgage') {
            setOverallStrategy(prev => ({ ...prev, reverseMortgage: true, heas: false }));
        } else if (propertyStrategy === 'heas') {
            setOverallStrategy(prev => ({ ...prev, reverseMortgage: false, heas: true }));
        } else {
            setOverallStrategy(prev => ({ ...prev, reverseMortgage: false, heas: false }));
        }
    }, [propertyStrategy]);
    
    // Reset function to clear all strategies after saving
    const resetStrategiesToBaseline = () => {
        
        // Clear any leftover restore strategy from sessionStorage
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
        }
        
        // Reset manual strategies
        setManualStrategies({
            gifting: 0,
            funeralBonds: 0,
            giftingSource: 'bank',
            funeralSource: 'bank'
        });
        
        // Reset property strategy
        setPropertyStrategy('none');
        
        // Reset property strategy details
        setDownsizeDetails({
            salePrice: factFind?.home?.value || 0,
            sellingCosts: (Number(factFind?.home?.value) || 0) * 0.025,
            newHomePurchasePrice: 0,
            downsizerContribution: 0,
            partnerDownsizerContribution: 0,
            giftingAmount: 0,
            funeralBondPurchase: 0,
            addedAssets: []
        });
        setRenovateDetails({
            renovationAmount: 0,
            fundingSources: []
        });
        setUpgradeDetails({
            currentHomeValue: parseFloat(String(factFind?.home?.value)) || 0,
            newHomePurchasePrice: 0,
            sellingCosts: 0,
            fundingSources: []
        });
        setReverseMortgageDetails({
            loanAmount: 0,
            loanPercentage: 0,
            interestRate: REVERSE_MORTGAGE_DEFAULT_RATE,
            loanStructure: 'lump_sum',
            regularPaymentAmount: 0,
            regularPaymentFrequency: 'fortnightly',
            establishmentFees: 0
        });
        setHeasDetails({
            paymentAmount: 0,
            paymentFrequency: 'fortnightly',
            interestRate: 3.95
        });
        
        // Reset overall strategy flags
        setOverallStrategy({});
        
        // Reset funding strategies
        setSelectedFundingStrategy(null);
        setAnnuityRecord(null);
        
        // Reset aged care integration
        setSelectedAgedCareStrategyId(null);
    };
    
    // Trigger reset when resetTrigger changes
    useEffect(() => {
        if (resetTrigger > 0) {
            resetStrategiesToBaseline();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetTrigger]);
    
    // Calculate BEFORE baseline (original position)
    const [beforeFinancials, setBeforeFinancials] = useState<SahFinancialsSnapshot | null>(null);
    useEffect(() => {
        if (!factFind) { setBeforeFinancials(null); return undefined; }
        let cancelled = false;
        calculationApi.calculateFinancials(factFind).then((r) => { if (!cancelled) setBeforeFinancials(r); });
        return () => { cancelled = true; };
    }, [factFind]);



    /*
     * ONE service plan for BOTH modelling modes (owner-caught asymmetry, 2026-08-25): the
     * Suggestion Engine resolved a plan (Service Builder's current plan, else the newest saved
     * one, else an illustrative package) while Manual Control silently read only
     * factFind.servicePlan — and modelled ZERO service costs when it was empty. Both modes now
     * consume this resolution, and the case line above the mode chooser SAYS which plan is in
     * use, so building the service plan first or the strategy first both read sensibly. The
     * sections unmount on tab switch, so a mount-time read of storage is always current.
     */
    /** Bumped when a plan is picked in-place below, so the resolution re-reads storage. */
    const [planPickVersion, setPlanPickVersion] = useState(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const allSavedServicePlans = useMemo(() => listSavedServicePlans(), [planPickVersion]);
    // Only plans with services are offered — an empty one resolves to "no plan", so picking it
    // snapped the control straight back (owner-caught, 2026-09-13). The empty ones are named below.
    const savedServicePlans = useMemo(() => usableSavedServicePlans(allSavedServicePlans), [allSavedServicePlans]);
    const emptySavedServicePlans = useMemo(
        () => allSavedServicePlans.filter((p) => !savedServicePlans.includes(p)),
        [allSavedServicePlans, savedServicePlans],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const servicePlanResolution = useMemo<{ plan: SupportAtHomeServicePlan | null; origin: ServicePlanSource }>(() => {
        const stored = getServicePlanWithSource();
        if (hasUsableServicePlan(stored.plan as unknown as SupportAtHomeServicePlan)) {
            return { plan: stored.plan as unknown as SupportAtHomeServicePlan, origin: stored.source };
        }
        /*
         * "NO SERVICE PLAN" MEANS NO SERVICE PLAN (owner-caught twice, 2026-08-27 and 2026-09-24).
         * Picking it cleared the session's current plan — and this resolution then fell through to
         * the newest saved plan, and failing that to the plan saved on the profile, so the picker
         * snapped back to "Service Plan A" the moment it was let go. The choice is recorded now
         * (`chosen`) and honoured here, before anything is restored. sahOptimiseServicePlanChoice
         * .test.ts and servicePlanStorage.test.ts hold it.
         */
        if (stored.source.kind === 'none' && stored.source.chosen) {
            return { plan: null, origin: stored.source };
        }
        const fromFactFind = factFind?.servicePlan as unknown as SupportAtHomeServicePlan | undefined;
        if (hasUsableServicePlan(fromFactFind)) {
            return { plan: fromFactFind as SupportAtHomeServicePlan, origin: { kind: 'restore', name: 'the plan saved on the profile' } };
        }
        return { plan: null, origin: { kind: 'none' } };
    }, [factFind, planPickVersion]);
    const servicePlanSource = servicePlanResolution.plan;
    const resolvedServicePlan = useMemo(
        () => resolveServicePlanForSuggestionEngine(servicePlanSource, isPartnered),
        [servicePlanSource, isPartnered],
    );


    /**
     * Which entry the service-plan picker should be showing.
     *
     * Derived from the resolution rather than held as its own state: the plan can change from the
     * Service Builder, from a restore, or from the picker itself, and a control with its own idea of
     * the answer would drift from the sentence beside it — which is exactly what it did while pinned
     * to "choose…".
     */
    const selectedSavedPlanId = useMemo(() => {
        if (resolvedServicePlan.isEstimated) return '';
        const origin = servicePlanResolution.origin;
        const name =
            origin.kind === 'saved' ? origin.name
            : origin.kind === 'current' ? origin.basedOnName
            : origin.kind === 'restore' ? origin.name
            : undefined;
        const match = name ? savedServicePlans.find((p) => p.name === name) : undefined;
        // A real plan with no saved counterpart is unsaved builder selections, not "none".
        return match ? match.id : UNSAVED_PLAN_OPTION;
    }, [resolvedServicePlan, servicePlanResolution, savedServicePlans]);

    /**
     * The resolved plan WITH its real category split attached.
     *
     * supportAtHomeContributionEngine reads `serviceDistribution` to decide what each contribution
     * rate is charged on, and assumes 40/35/25 of the whole budget when there isn't one. The plan
     * this screen hands it is `{ client, partner }` — neither `serviceDistribution` nor `services` —
     * so that fallback fired even for a real, built plan, and Optimise quoted the assumed split
     * while the Service Builder priced the actual services. One plan, two answers (owner-caught,
     * 2026-08-27: $7,722 here against $0 there).
     *
     * Every call that prices contributions goes through this, so the two screens cannot disagree.
     */
    const planForPricing = useMemo(
        () => ({
            ...(resolvedServicePlan.plan as Record<string, unknown>),
            serviceDistribution: servicePlanDistribution(resolvedServicePlan.plan as never),
        }),
        [resolvedServicePlan],
    );

    /**
     * The plan a strategy saved RIGHT NOW would be priced against.
     *
     * Stamped onto every saved strategy so a later change of services can be detected. A Support at
     * Home strategy's dollars are `budget x rate`: optimising moves the rate, the Service Builder
     * moves the budget, so the rates survive a service change and the dollars do not.
     */
    const currentServicePlanBasis = useMemo(
        () => describeServicePlan(resolvedServicePlan.plan as never, resolvedServicePlan.isEstimated),
        [resolvedServicePlan],
    );

    /** How big the real plan is — so the case line can IDENTIFY it, not just gesture at it. */
    const servicePlanStats = useMemo(() => {
        if (resolvedServicePlan.isEstimated) return null;
        const count = (side: Record<string, { quantity?: number | string } | undefined> | undefined) =>
            Object.values(side || {}).filter((d) => d && Number(d.quantity) > 0).length;
        const value = (side: Record<string, { quantity?: number | string; frequency?: string } | undefined> | undefined) =>
            computeServiceCostsForPerson(side || {}, {}, SUPPORT_AT_HOME_SERVICES).total;
        return {
            serviceCount: count(resolvedServicePlan.plan.client) + count(resolvedServicePlan.plan.partner),
            annualValue: value(resolvedServicePlan.plan.client) + value(resolvedServicePlan.plan.partner),
        };
    }, [resolvedServicePlan]);

    const [beforeContributions, setBeforeContributions] = useState<SahContributionsResult | null>(null);
    useEffect(() => {
        if (!beforeFinancials) { setBeforeContributions(null); return undefined; }
        let cancelled = false;
        // Use the same participant type settings as the after calculation
        // The resolved plan (real or illustrative) rides on the fact-find copy, so before and
        // after are priced on the SAME services.
        const beforeFactFind = { ...factFind, servicePlan: planForPricing as never };
        calculationApi.calculateIndividualContributions(beforeFactFind, beforeFinancials, participantType, nwopCategory, meansDisclosed, Number(previousITCF) || 0).then((r) => {
            if (!cancelled) setBeforeContributions(r);
        });
        return () => { cancelled = true; };
    }, [beforeFinancials, factFind, participantType, nwopCategory, meansDisclosed, previousITCF, resolvedServicePlan]);

    const beforeContributionData = isPartnered
        ? (beforeContributions?.combined || beforeContributions?.client)
        : beforeContributions?.client;

    // Calculate optimized scenario with all strategies applied (sahPlanFactFind.ts)
    const optimizedScenario = useMemo(() => {
        if (!factFind) return null;
        return simulateManualSahPlan(factFind, {
            manualStrategies, propertyStrategy, downsizeDetails, renovateDetails, upgradeDetails, overallStrategy,
            reverseMortgageDetails, selectedAgedCareIntegration, selectedFundingStrategy, annuityRecordForCalculation, beforeFinancials,
        });
    }, [factFind, manualStrategies, propertyStrategy, downsizeDetails, renovateDetails, upgradeDetails, overallStrategy, reverseMortgageDetails, selectedAgedCareIntegration, selectedFundingStrategy, annuityRecordForCalculation, beforeFinancials]);

    const [afterFinancials, setAfterFinancials] = useState<SahFinancialsSnapshot | null>(null);
    useEffect(() => {
        if (!optimizedScenario?.optimizedFactFind) { setAfterFinancials(null); return undefined; }

        const { optimizedFactFind, agedCareAnnualCost } = optimizedScenario;

        const strategyOptionsPayload: Record<string, unknown> = {
            reverseMortgage: overallStrategy.reverseMortgage,
            reverseMortgageDetails: overallStrategy.reverseMortgage ? reverseMortgageDetails : null,
            heas: overallStrategy.heas,
            heasDetails: overallStrategy.heas ? heasDetails : null,
            integratedAgedCareCost: agedCareAnnualCost,
            integratedAgedCareDetails: selectedAgedCareIntegration && agedCareAnnualCost > 0 ? selectedAgedCareIntegration : null,
            ...agedCareResidencyOverrides
        };

        if (selectedFundingStrategy === 'annuity' && annuityRecordForCalculation) {
            strategyOptionsPayload.annuities = [annuityRecordForCalculation];
        }

        let cancelled = false;
        calculationApi.calculateFinancials(optimizedFactFind, strategyOptionsPayload).then((r) => {
            if (!cancelled) setAfterFinancials(r);
        });
        return () => { cancelled = true; };
    }, [optimizedScenario, overallStrategy, reverseMortgageDetails, heasDetails, selectedAgedCareIntegration, selectedFundingStrategy, annuityRecordForCalculation, agedCareResidencyOverrides]);
    
    // Save optimized financials to sessionStorage only in manual mode (avoid SE/manual cross-leak to Service Builder)
    useEffect(() => {
        if (internalStrategyMode !== 'manual') return;
        if (typeof window !== 'undefined' && afterFinancials) {
            try {
                sessionStorage.setItem('careCompanionOptimizedFinancials', JSON.stringify({
                    financials: afterFinancials,
                    timestamp: Date.now()
                }));
                // Dispatch custom event to notify other components (same tab) - use setTimeout to avoid render issues
                setTimeout(() => {
                    window.dispatchEvent(new Event('optimizedFinancialsUpdated'));
                }, 0);
            } catch (error) {
                console.error('Error saving optimized financials:', error);
            }
        } else if (typeof window !== 'undefined' && !afterFinancials) {
            // Clear if no strategies applied
            try {
                sessionStorage.removeItem('careCompanionOptimizedFinancials');
                setTimeout(() => {
                    window.dispatchEvent(new Event('optimizedFinancialsUpdated'));
                }, 0);
            } catch (error) {
                console.error('Error clearing optimized financials:', error);
            }
        }
    }, [afterFinancials, internalStrategyMode]);
    
    // Check if any strategies are actually applied
    const hasStrategies = useMemo(() => {
        return Number(manualStrategies.gifting) > 0 ||
               Number(manualStrategies.funeralBonds) > 0 ||
               propertyStrategy !== 'none' ||
               overallStrategy.reverseMortgage ||
               overallStrategy.heas ||
               Boolean(selectedAgedCareIntegration) ||
               selectedFundingStrategy === 'annuity';
    }, [manualStrategies, propertyStrategy, overallStrategy, selectedAgedCareIntegration, selectedFundingStrategy]);
    
    const [afterContributions, setAfterContributions] = useState<SahContributionsResult | null>(null);
    useEffect(() => {
        // If no strategies are applied, use beforeContributions (which matches ContributionsSection exactly)
        if (!hasStrategies && beforeContributions) {
            setAfterContributions(beforeContributions);
            return undefined;
        }

        if (!afterFinancials || !optimizedScenario?.optimizedFactFind) { setAfterContributions(null); return undefined; }

        const hasAcatLevel = factFind?.careNeeds?.clientAcat?.status === 'yes' && factFind?.careNeeds?.clientAcat?.homeCareLevel;
        const hasPartnerAcatLevel = factFind?.adviceFor === 'couple' && factFind?.careNeeds?.partnerAcat?.status === 'yes' && factFind?.careNeeds?.partnerAcat?.homeCareLevel;

        if (!hasAcatLevel && !hasPartnerAcatLevel) {
            setAfterContributions(null);
            return undefined;
        }

        // Strategies are applied - use optimizedFactFind (which has strategy changes)
        const baseFactFind = structuredClone(optimizedScenario.optimizedFactFind);
        // The SAME resolved plan as the before call and the Suggestion Engine.
        baseFactFind.servicePlan = planForPricing as never;

        let cancelled = false;
        calculationApi.calculateIndividualContributions(
            baseFactFind,
            afterFinancials,
            participantType,
            nwopCategory,
            meansDisclosed,
            Number(previousITCF) || 0,
            0.5,
            { strategyOptions: agedCareResidencyOverrides }
        ).then((r) => { if (!cancelled) setAfterContributions(r); });
        return () => { cancelled = true; };
    }, [hasStrategies, beforeContributions, afterFinancials, optimizedScenario, factFind, participantType, nwopCategory, meansDisclosed, previousITCF, agedCareResidencyOverrides, resolvedServicePlan]);
    
    // Get contribution data for display (needed for projections)
    const afterContributionData = isPartnered 
        ? (afterContributions?.combined || afterContributions?.client)
        : afterContributions?.client;
    
    // Calculate projections for legacy estimate
    // Calculate combined care costs separately so it's available for the modal
    const combinedCareCosts = useMemo<CombinedCareCosts | null>(() => {
        if (!afterContributionData) return null;

        // Check if services are actually selected (not just using example distribution)
        // A resolved plan (real or illustrative) is always present now — see resolvedServicePlan.
        const hasServicePlan = true;

        const hasBudget = (afterContributionData?.budget?.total ?? 0) > 0 && hasServicePlan;
        const annualContribution = hasBudget ? (afterContributionData.contributionAmounts?.totalAnnual || 0) : 0;
        const agedCareAnnualCost = selectedAgedCareIntegration?.annualCost || 0;

        const supportAtHomeDaily = annualContribution / 365;
        const agedCareDaily = agedCareAnnualCost / 365;

        return {
            totalDailyCost: supportAtHomeDaily + agedCareDaily,
            supportAtHomeDailyCost: supportAtHomeDaily,
            agedCareDailyCost: agedCareDaily,
            supportAtHomeAnnualCost: annualContribution,
            agedCareAnnualCost
        };
    }, [afterContributionData, selectedAgedCareIntegration, factFind]);

    /** Care costs for pre-strategy FY blend: profile financials + before optimisation contributions (no linked RAC annual from strategy integration). */
    const combinedCareCostsBaseline = useMemo<CombinedCareCosts | null>(() => {
        if (!beforeContributionData) return null;

        // A resolved plan (real or illustrative) is always present now — see resolvedServicePlan.
        const hasServicePlan = true;

        const hasBudget = (beforeContributionData?.budget?.total ?? 0) > 0 && hasServicePlan;
        const annualContribution = hasBudget ? (beforeContributionData.contributionAmounts?.totalAnnual || 0) : 0;
        const agedCareAnnualCost = 0;

        const supportAtHomeDaily = annualContribution / 365;
        const agedCareDaily = agedCareAnnualCost / 365;

        return {
            totalDailyCost: supportAtHomeDaily + agedCareDaily,
            supportAtHomeDailyCost: supportAtHomeDaily,
            agedCareDailyCost: agedCareDaily,
            supportAtHomeAnnualCost: annualContribution,
            agedCareAnnualCost,
        };
    }, [beforeContributionData, factFind]);

    /**
     * The shared debt strategy — see the Aged Care module. Handed to the ENGINE via the projection
     * options; the engine performs the payoff at the projection's start (a lender takes the money any
     * day), the loan-repayment plugin stops servicing whatever is cleared, and the projections and
     * their charts reflect it without this page doing any arithmetic of its own.
     */
    /**
     * When Support at Home services BEGIN (`YYYY-MM-DD`), blank = already receiving them.
     *
     * A fact about the case, not about any one strategy — so it is passed to the manual projection
     * AND down into the Suggestion Engine, where the server merges it over the baseline and every
     * candidate alike. Comparing strategies that assumed different start dates would compare nothing.
     *
     * The engine treats it exactly as the aged-care entry date treats care fees: nothing is charged
     * before the day. The stay-at-home side effects of that flag were audited before this was wired —
     * living expenses do not switch (no in-care figure is set) and the home-letting clock never starts
     * (rentHome is never set here) — so the date means "support costs start here" and nothing else.
     */
    const supportStartInPast = supportStartDate !== '' && supportStartDate < new Date().toISOString().slice(0, 10);

    const [debtPayments, setDebtPayments] = useState<Record<string, number>>({});
    const [debtFunding, setDebtFunding] = useState<Record<string, number>>({});
    const [debtRepaymentBasis, setDebtRepaymentBasis] = useState<'keep_repayment' | 'lower_repayment'>('keep_repayment');
    const debtPayoffPlan = useMemo<DebtPayoff | null>(() => {
        const payments = Object.fromEntries(Object.entries(debtPayments).filter(([, amount]) => (Number(amount) || 0) > 0));
        if (Object.keys(payments).length === 0) return null;
        const funding = Object.fromEntries(Object.entries(debtFunding).filter(([, amount]) => (Number(amount) || 0) > 0));
        return { payments, funding, repaymentBasis: debtRepaymentBasis };
    }, [debtPayments, debtFunding, debtRepaymentBasis]);

    const sahProjectionStrategyOptions = useMemo<Record<string, unknown>>(() => {
        const strategyOptions: Record<string, unknown> = {
            reverseMortgage: overallStrategy.reverseMortgage,
            reverseMortgageDetails: overallStrategy.reverseMortgage ? reverseMortgageDetails : null,
            heas: overallStrategy.heas,
            heasDetails: overallStrategy.heas ? heasDetails : null,
            inflationAssumptionPct: 2.5,
            projectionPeriodMode: 'financial_year',
            ...agedCareResidencyOverrides,
        };

        if (selectedAgedCareIntegration) {
            strategyOptions.integratedAgedCareDetails = selectedAgedCareIntegration;
            if (selectedAgedCareIntegration.livingExpenses !== undefined && selectedAgedCareIntegration.livingExpenses !== null) {
                strategyOptions.scenarioLivingExpenses = selectedAgedCareIntegration.livingExpenses;
            }
        }

        if (selectedFundingStrategy === 'annuity' && annuityRecordForCalculation) {
            strategyOptions.annuities = [annuityRecordForCalculation];
        }

        if (debtPayoffPlan) strategyOptions.debtPayoff = debtPayoffPlan;

        // A past date means support is already under way, which is what a BLANK date means — sending
        // it would defer costs to a day already gone.
        if (supportStartDate && !supportStartInPast) strategyOptions.careStartDate = supportStartDate;

        return strategyOptions;
    }, [
        debtPayoffPlan,
        supportStartDate,
        supportStartInPast,
        overallStrategy.reverseMortgage,
        overallStrategy.heas,
        reverseMortgageDetails,
        heasDetails,
        agedCareResidencyOverrides,
        selectedAgedCareIntegration,
        selectedFundingStrategy,
        annuityRecordForCalculation,
    ]);

    const [projections, setProjections] = useState<SahProjectionYear[] | null>(null);
    useEffect(() => {
        if (!afterFinancials || !afterContributionData || !optimizedScenario?.optimizedFactFind || !combinedCareCosts) { setProjections(null); return undefined; }

        let cancelled = false;
        calculationApi.runProjections(optimizedScenario.optimizedFactFind, sahProjectionStrategyOptions, combinedCareCosts, legacyYears, { debug: false }).then((proj) => {
            if (!cancelled) setProjections(proj);
        });
        return () => { cancelled = true; };
    }, [optimizedScenario, afterFinancials, afterContributionData, legacyYears, sahProjectionStrategyOptions, combinedCareCosts]);

    /*
     * THE FINANCIAL-YEAR / CALENDAR-YEAR TOGGLE (owner, 23 Sep 2026: present in Aged Care, missing
     * everywhere else). Not a display filter — the engine PROJECTS the year it REPORTS, so the rows
     * are cut again by a second run, which is exactly what the shared modal gates the toggle on.
     * Fetched only while the modal is open, so a remembered 'calendar_year' does not fire a second
     * run on every page load. The mode is the modal's own sticky preference, shared with Aged Care.
     */
    const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
    const [calendarYearProjections, setCalendarYearProjections] = useState<SahProjectionYear[] | null>(null);
    useEffect(() => {
        if (modalPeriodMode !== 'calendar_year') { setCalendarYearProjections(null); return undefined; }
        if (!showProjectionsModal) return undefined;
        if (!optimizedScenario?.optimizedFactFind || !combinedCareCosts) return undefined;
        let cancelled = false;
        setCalendarYearProjections(null);
        calculationApi.runProjections(
            optimizedScenario.optimizedFactFind,
            { ...sahProjectionStrategyOptions, projectionPeriodMode: 'calendar_year' },
            combinedCareCosts,
            legacyYears,
            { debug: false },
        ).then((proj) => { if (!cancelled) setCalendarYearProjections(proj); });
        return () => { cancelled = true; };
    }, [showProjectionsModal, modalPeriodMode, optimizedScenario, sahProjectionStrategyOptions, combinedCareCosts, legacyYears]);

    /** Adds FY full-year pre/post blend on row 1 (same pattern as Aged Care ProjectionsModal). */
    /*
     * The benchmark: this household RECEIVING THE SAME SERVICES with no financial strategy, projected
     * over the same period. Support at Home is a care requirement, so "do nothing" still pays for
     * care — which is why the baseline carries combinedCareCostsBaseline rather than zero costs.
     */
    const sahDoNothingLegacy = useDoNothingLegacy(factFind, legacyYears, {
        enabled: !!combinedCareCostsBaseline,
        agedCareCosts: (combinedCareCostsBaseline ?? undefined) as unknown as Record<string, unknown>,
    });

    const [projectionsForModal, setProjectionsForModal] = useState<SahProjectionYear[] | null>(null);

    /**
     * How long the money lasts with NO strategy — the starting position's runway.
     *
     * Reads the same projection rows the panel does, through the same shared helper Aged Care uses,
     * so the anchor note and the panel cannot report different years. Null when there is nothing to
     * project yet, in which case the beat is simply omitted.
     */
    const startingRunway = useMemo(
        () => planRunwayCopy(projections as never, Number(legacyYears) || 5),
        [projections, legacyYears],
    );
    useEffect(() => {
        if (!projections?.length || !combinedCareCosts || !optimizedScenario?.optimizedFactFind) { setProjectionsForModal(projections); return undefined; }

        const mode = sahProjectionStrategyOptions.projectionPeriodMode ?? 'financial_year';
        if (mode === 'strategy_year') { setProjectionsForModal(projections); return undefined; }

        const baselineCosts = combinedCareCostsBaseline ?? {
            totalDailyCost: 0,
            supportAtHomeDailyCost: 0,
            agedCareDailyCost: 0,
            supportAtHomeAnnualCost: 0,
            agedCareAnnualCost: 0,
        };

        let cancelled = false;
        calculationApi.computeYearOneFullAustralianFyBlendSupportAtHome({
            strategyOptions: sahProjectionStrategyOptions,
            baselineFactFind: factFind,
            strategyFactFind: optimizedScenario.optimizedFactFind,
            baselineCombinedCareCosts: baselineCosts,
            strategyCombinedCareCosts: combinedCareCosts,
            strategyYearOneProjection: projections[0],
            initialFinancials: beforeFinancials,
            currentFinancials: afterFinancials,
            legacyYears,
            options: { debug: false },
        }).then((fyView) => {
            if (cancelled) return;
            setProjectionsForModal(projections.map((row, i) => (i === 0 ? { ...row, fyFullYearView: fyView } : row)));
        });
        return () => { cancelled = true; };
    }, [
        projections,
        combinedCareCosts,
        combinedCareCostsBaseline,
        optimizedScenario,
        factFind,
        beforeFinancials,
        afterFinancials,
        legacyYears,
        sahProjectionStrategyOptions,
    ]);
    
    // Calculate projected legacy from projections
    const projectedLegacy = useMemo<number>(() => {
        if (!projections || projections.length === 0) return afterFinancials?.netWorth || 0;
        const lastYear = projections[projections.length - 1];
        return lastYear.netWorth || 0;
    }, [projections, afterFinancials]);

    const formatCurrency = (value: number | string | null | undefined) => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value) || 0);
    };

    const formatPercent = (value: number | undefined) => {
        return `${((value ?? 0) * 100).toFixed(1)}%`;
    };

    // CalculationTooltip is now defined at module level, accessible to both modal and StrategiesSection

    /*
     * Support at Home used its OWN copy of ImpactMetric — a different design that showed only the
     * AFTER figure with a delta beside it, never the baseline it was measured against. It is now the
     * shared component (owner-guided, 2026-08-26: build it correctly and make it scalable).
     *
     * Two things follow. The module stops looking like a different product from the other four, and
     * every improvement to the shared tile — the before-arrow-after grammar, the row alignment, the
     * named benchmark — reaches it without being rebuilt here. A private copy of a shared component
     * is how one module quietly falls behind and how the same fix comes to cost twice.
     */

    const serviceCosts = null; // Will be calculated if needed
    
    // Prepare contribution rates with before/after data for Insight Panel
    const contributionRatesForInsight = {
        before: beforeContributionData?.contributionRates || {},
        after: afterContributionData?.contributionRates || {}
    };

    const isSuggestionMode = internalStrategyMode === 'suggestion';

    /**
     * What the plan summary is describing, in the client's words — or null when it is describing
     * nothing yet, in which case the panel says so and holds the starting position.
     *
     * Derived from the strategies actually switched ON, never a stored label, for the same reason
     * Aged Care does it that way: a panel headed with a name that no longer matches the figures
     * underneath it is worse than one with no name at all.
     */
    const sahPlanSummaryName = useMemo<string | null>(() => {
        if (isSuggestionMode) {
            return (sahSeSelectedStrategy as { name?: string } | null)?.name ?? null;
        }
        const parts: string[] = [];
        /*
         * EVERY LEVER `hasStrategies` counts is named here (owner-caught, 2026-09-24: a $10,000 gift
         * moved every figure in the panel while its heading still read "No plan yet — the starting
         * position"). A heading that says nothing is happening over figures that have moved is the
         * one thing this panel must never do.
         */
        if (Number(manualStrategies.gifting) > 0) parts.push(`Gift ${formatCurrency(Number(manualStrategies.gifting))}`);
        if (Number(manualStrategies.funeralBonds) > 0) parts.push(`Buy a funeral bond for ${formatCurrency(Number(manualStrategies.funeralBonds))}`);
        if (propertyStrategy === 'downsize') parts.push('Downsize the home');
        if (propertyStrategy === 'renovate') parts.push('Renovate the home');
        if (propertyStrategy === 'upgrade') parts.push('Upgrade the home');
        if (propertyStrategy === 'reverseMortgage') parts.push('Take a reverse mortgage');
        if (propertyStrategy === 'heas') parts.push('Use the Home Equity Access Scheme');
        if (annuityRecord) parts.push('Commence an annuity');
        if (selectedAgedCareStrategyId) parts.push('Link an aged care plan');
        if (!parts.length) return null;
        const [lead, ...rest] = parts;
        return rest.length
            ? `${lead}, ${rest.map((p) => p.charAt(0).toLowerCase() + p.slice(1)).join(', ')}`
            : lead;
    }, [isSuggestionMode, sahSeSelectedStrategy, manualStrategies, propertyStrategy, annuityRecord, selectedAgedCareStrategyId]);

    const panelContributionData = isSuggestionMode ? beforeContributionData : afterContributionData;
    const panelFinancials = isSuggestionMode ? beforeFinancials : afterFinancials;
    const impactAfterFinancials = isSuggestionMode ? beforeFinancials : afterFinancials;
    const impactProjectedLegacy = isSuggestionMode ? (beforeFinancials?.netWorth || 0) : projectedLegacy;
    const showContributionWas = !isSuggestionMode;

    /*
     * CASHFLOW NET OF THE SERVICE PLAN (owner-caught, 2026-09-24). The annual position's cashflow
     * is "money in, less money out" BEFORE any care costs — the standalone assessment never sees
     * the Support at Home contribution, which the projection charges as a monthly stream instead.
     * So with "Service Plan A" selected the panel still read $10,198 while the Service Builder, one
     * tab back, read $4,214 remaining after the $5,984 contribution: same client, same plan, two
     * cashflows. The panel now nets the plan's contribution the way the Service Builder does — the
     * share of care costs shown in the tile directly above it — before AND after, so the movement
     * between them is the strategy's alone, and year one of the projection and the annual position
     * finally agree. No service plan, no dollars: on the illustrative package the contribution is
     * not quoted (see sahInBrief), so the cashflow stays gross and the starting-position note says
     * "before any care costs" and stops there.
     */
    const servicePlanName = useMemo<string | null>(() => {
        const o = servicePlanResolution.origin;
        return o.kind === 'saved' ? o.name : o.kind === 'current' ? (o.basedOnName ?? null) : o.kind === 'restore' ? o.name : null;
    }, [servicePlanResolution]);
    const sahContributionBefore = !resolvedServicePlan.isEstimated ? (beforeContributionData?.contributionAmounts?.totalAnnual ?? null) : null;
    const sahContributionAfter = !resolvedServicePlan.isEstimated ? (panelContributionData?.contributionAmounts?.totalAnnual ?? null) : null;
    const sahCashFlowBefore = beforeFinancials?.cashFlow == null ? null : beforeFinancials.cashFlow - (sahContributionBefore ?? 0);
    const sahCashFlowAfter = impactAfterFinancials?.cashFlow == null ? null : impactAfterFinancials.cashFlow - (sahContributionAfter ?? 0);
    const sahCashFlowBreakdown = useMemo<BreakdownRow[] | null>(() => {
        const rows = impactAfterFinancials?.cashFlowBreakdown;
        if (!rows || sahContributionAfter == null) return null;
        return [
            ...rows,
            { label: '--- Support at Home ---', value: '' },
            { label: `Less: Your share of care costs${servicePlanName ? ` (${servicePlanName})` : ''}`, value: `-${formatCurrency(sahContributionAfter)}` },
        ];
    }, [impactAfterFinancials, sahContributionAfter, servicePlanName]);

    /**
     * The panel's "In brief" — one sentence saying what the plan does, in the same grammar Aged
     * Care uses ("what it costs, then each figure that moved and by how much").
     *
     * Built ONLY from figures already rendered in the tiles directly beneath it, subtracted exactly
     * as the tiles subtract them, so the sentence cannot disagree with the numbers it summarises.
     * Nothing here re-reads the fact-find or calls an engine.
     *
     * The Support at Home difference from Aged Care is what leads: this module charges a SHARE of
     * each service rather than a room, so the client's own contribution is the cost that moves, and
     * it leads the sentence where the DAP leads Aged Care's.
     */
    const sahInBrief = useMemo<React.ReactNode | null>(() => {
        if (!sahPlanSummaryName) return null;
        const contribBefore = beforeContributionData?.contributionAmounts?.totalAnnual ?? null;
        const contribAfter = afterContributionData?.contributionAmounts?.totalAnnual ?? null;
        const pensionDelta = (impactAfterFinancials?.socialSecurity ?? 0) - (beforeFinancials?.socialSecurity ?? 0);
        const cashflowDelta = (sahCashFlowAfter ?? 0) - (sahCashFlowBefore ?? 0);
        const legacyDelta =
            sahDoNothingLegacy != null && impactProjectedLegacy != null
                ? impactProjectedLegacy - sahDoNothingLegacy
                : null;

        const clauses: RecapClause[] = [];
        /*
         * NO DOLLARS WITHOUT A SERVICE PLAN. Out-of-pocket is budget x rate, and until a plan exists
         * the budget is the illustrative package's. Quoting "your share falls from $7,722 to $3,929"
         * against services nobody has chosen puts a placeholder in the sentence a client remembers.
         * The RATE movement is real either way, so that is what leads instead.
         */
        if (resolvedServicePlan.isEstimated) {
            const rateBefore = beforeContributionData?.contributionRates?.everydayLiving;
            const rateAfter = afterContributionData?.contributionRates?.everydayLiving;
            if (rateBefore != null && rateAfter != null && rateBefore !== rateAfter) {
                // A FALLING rate is good news here — the client pays a smaller share.
                clauses.push({
                    tone: rateAfter < rateBefore ? 'good' : 'bad',
                    text: `the everyday living rate ${rateAfter < rateBefore ? 'falls' : 'rises'} from ${(rateBefore * 100).toFixed(1)}% to ${(rateAfter * 100).toFixed(1)}% (the dollars follow once a service plan is built)`,
                });
            }
        } else if (contribBefore != null && contribAfter != null && Math.round(contribBefore - contribAfter) !== 0) {
            const down = contribAfter < contribBefore;
            clauses.push({
                term: 'your share of care costs',
                tone: down ? 'good' : 'bad',
                text: `your share of care costs ${down ? 'falls' : 'rises'} from ${formatCurrency(contribBefore)} to ${formatCurrency(contribAfter)} a year`,
            });
        } else if (contribAfter != null) {
            clauses.push(plainClause(`your share of care costs stays at ${formatCurrency(contribAfter)} a year`));
        }
        clauses.push(pensionClause(pensionDelta));
        if (Math.round(cashflowDelta) !== 0) {
            clauses.push(cashflowClause(cashflowDelta, sahCashFlowAfter));
        }
        if (legacyDelta != null) clauses.push(estateClause(legacyDelta, Number(legacyYears) || 0));
        if (!clauses.length) return null;
        return <RecapClauses clauses={clauses} />;
    }, [
        sahPlanSummaryName,
        beforeContributionData,
        afterContributionData,
        impactAfterFinancials,
        beforeFinancials,
        sahDoNothingLegacy,
        impactProjectedLegacy,
        legacyYears,
        resolvedServicePlan,
        sahCashFlowAfter,
        sahCashFlowBefore,
    ]);
    /**
     * The plain-words recap, built once and read inside "Understanding your plan".
     *
     * The SAME component with the SAME props it had when it sat inline below the strategy cards —
     * only its position changed, matching the Aged Care benchmark. Inline, each of its reasons was
     * hidden behind a "(why?)" tap and the card ran the width of the page; on its own surface the
     * reasons are simply visible, which is what a reader wanted from them. It also stops the
     * Optimise page growing a second full-width explanation below a rail that already summarises.
     */
    const sahRecapNode = (
        <SahPlanRecap
            before={beforeFinancials}
            after={afterFinancials}
            contributionBefore={beforeContributionData?.contributionAmounts?.totalAnnual || 0}
            contributionAfter={afterContributionData?.contributionAmounts?.totalAnnual || 0}
            ratesBefore={beforeContributionData?.contributionRates || null}
            ratesAfter={afterContributionData?.contributionRates || null}
            integration={integrationSummary}
            gifting={Number(manualStrategies.gifting) || 0}
            funeralBonds={Number(manualStrategies.funeralBonds) || 0}
            propertyStrategy={propertyStrategy}
            /*
             * The loan, measured against the SAME projection the panel beside it shows — the closing
             * balance is read off its final row rather than recomputed, so the warning and the estate
             * figure it explains cannot disagree.
             */
            equityRelease={(() => {
                const rows = projections || [];
                const last = (rows[rows.length - 1] || {}) as Record<string, unknown>;
                if (propertyStrategy === 'reverseMortgage' && Number(reverseMortgageDetails.loanAmount) > 0) {
                    return {
                        kind: 'reverseMortgage',
                        loanAmount: Number(reverseMortgageDetails.loanAmount) || 0,
                        interestRate: Number(reverseMortgageDetails.interestRate) || 0,
                        projectedBalance: Number(last.reverseMortgageLoanBalance) || 0,
                        years: rows.length,
                    };
                }
                if (propertyStrategy === 'heas' && Number(heasDetails.paymentAmount) > 0) {
                    const perYear = heasDetails.paymentFrequency === 'fortnightly'
                        ? Number(heasDetails.paymentAmount) * 26
                        : Number(heasDetails.paymentAmount) * 12;
                    return {
                        kind: 'heas',
                        loanAmount: Math.round(perYear * rows.length),
                        interestRate: Number(heasDetails.interestRate) || 0,
                        projectedBalance: Number(last.heasLoanBalance) || 0,
                        years: rows.length,
                    };
                }
                return null;
            })()}
            downsizeUnallocated={propertyStrategy === 'downsize' ? computeSahDownsizeUnallocated(factFind, downsizeDetails as never) : 0}
            reverseMortgage={!!overallStrategy.reverseMortgage}
            heas={!!overallStrategy.heas}
            annuitySelected={selectedFundingStrategy === 'annuity'}
            /*
             * The annuity's own terms, from the record the impact is calculated on — the same object
             * the projection uses, so the disclosure cannot describe a different annuity from the one
             * being modelled (owner, 2026-09-01).
             */
            annuityTerms={
                annuityRecordForCalculation
                    ? {
                          purchaseAmount: Number(annuityRecordForCalculation.purchaseAmount) || 0,
                          residualPercent: Math.round(
                              Math.max(0, Math.min(1, Number(annuityRecordForCalculation.residualCapital) || 0)) * 100,
                          ),
                          owner: annuityRecordForCalculation.owner || 'client',
                          reversionary: !!annuityRecordForCalculation.reversionary,
                          indexed: !!annuityRecordForCalculation.indexation,
                          annuityType: annuityRecordForCalculation.annuityType || 'lifetime',
                      }
                    : null
            }
            factFind={factFind}
            hasStrategies={hasStrategies}
        />
    );
    const rightColumnBlurred = isSuggestionMode && !showBaseline;

    return (
        <>
            {/* Insight Panel */}
            
            {/* THE SAME 3/2 SPLIT AT THE SAME BREAKPOINT AS AGED CARE (2026-08-27). This was
                `lg:grid-cols-3` with a 1/3 rail: at a 1024px viewport that is a ~227px pane for a
                component whose internal breakpoints assume ~391px, so the Financial Impact heading
                and its projections link collapsed onto one line. `xl:` is the benchmark's choice and
                the reasoning is in agedCareResponsiveLayout.test.ts. */}
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-8 items-start">
                {/* Left Column - Main Content */}
                <div className="xl:col-span-3 space-y-6">
                {/*
                  * WHEN SUPPORT STARTS now lives in the Service Builder, and is stated here rather
                  * than asked again.
                  *
                  * The owner read it as a duplicate; it was not — this was the only copy. But the
                  * point behind it holds: when services begin is a property of the SERVICE PLAN, and
                  * the plan is built first now. Asking on the optimisation screen put a care fact
                  * among the financial levers, one screen after the place it belongs.
                  *
                  * It still matters here — it is the date the Suggestion Engine projects from, and
                  * an engine that silently assumed today would rank every candidate against the
                  * wrong timeline — so the value is read, shown, and linked back to where it is set.
                  */}
                {!sahSeAnalyzing && (
                    <div className="bg-white rounded-xl border border-gray-200 px-6 py-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-slate-700">
                            <span className="font-semibold text-slate-900">Support services start:</span>{' '}
                            {supportStartDate && !supportStartInPast
                                ? new Date(`${supportStartDate}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
                                : 'from today'}
                            {supportStartInPast && (
                                <span className="ml-2 text-xs text-amber-700">
                                    A past date models as “already receiving support”.
                                </span>
                            )}
                        </p>
                        <button
                            type="button"
                            onClick={() => onNavigateToBuilder?.()}
                            className="text-sm font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                        >
                            Change it in Service Builder ›
                        </button>
                    </div>
                )}

                {/* The starting position — the anchor every strategy below is compared against,
                    between the case facts and the mode choice (owner direction, 2026-08-25). The
                    insight panel's opening story, as a headline with the depth behind taps. */}
                {/* Fed from the SAME sources the panel beside it reports, so the anchor and the
                    figures it anchors cannot disagree: sahDoNothingLegacy is the estate tile's own
                    "before", and the runway reads the projection rows. */}
                {!sahSeAnalyzing && (
                    <SahStartingPosition
                        factFind={factFind}
                        before={beforeFinancials}
                        careContribution={sahContributionBefore != null ? { amount: sahContributionBefore, planName: servicePlanName } : null}
                        runwayHeadline={startingRunway?.headline ?? null}
                        runwayDetail={startingRunway?.flag ?? null}
                        startingLegacy={sahDoNothingLegacy}
                        legacyYears={legacyYears}
                        onLegacyYearsChange={setLegacyYears}
                    />
                )}

                {/* WHICH service plan the figures use — identified by size, with the
                    chicken-and-egg said in one reassuring sentence (owner copy pass, 2026-08-25). */}
                {/*
                  * The service plan every figure on this screen is priced on — given the same card
                  * anatomy as "Where Baldo stands today" (owner, 2026-08-27: "I think we can make
                  * this tile look much nicer... include a heading like we do for Where Baldo stands
                  * today"). It reads like a footnote and it is not one: it names the plan the whole
                  * screen depends on, and whether that plan is real or illustrative.
                  */}
                {!sahSeAnalyzing && (
                    <PlainWordsCard
                        eyebrow={resolvedServicePlan.isEstimated ? 'What these figures are priced on' : 'Your service plan'}
                        className="mb-0"
                    >
                        <PlainWordsBeat
                            /* Amber earns its place only when the basis is a placeholder — that IS a
                               "check this". A real plan is a plain statement of fact. */
                            tone={resolvedServicePlan.isEstimated ? 'warn' : 'info'}
                            heading={
                                resolvedServicePlan.isEstimated
                                    ? 'No service plan built yet — an illustrative package is standing in'
                                    : 'Priced on the plan you built'
                            }
                        >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <p className="min-w-0 leading-relaxed text-slate-700">
                                {resolvedServicePlan.isEstimated ? (
                                    <>
                                        <span className="font-semibold text-slate-900">No service plan built yet</span> — these
                                        figures use an illustrative typical package as the basis
                                        {resolvedServicePlan.description && (
                                            <> <JargonTerm term="(what's in it?)">{resolvedServicePlan.description}</JargonTerm></>
                                        )}.
                                    </>
                                ) : (
                                    <>
                                        These figures are priced on{' '}
                                        <span className="font-semibold text-slate-900">
                                            {servicePlanResolution.origin.kind === 'current'
                                                ? servicePlanResolution.origin.basedOnName
                                                    ? `the saved plan “${servicePlanResolution.origin.basedOnName}” (loaded in Service Builder)`
                                                    : 'the services currently selected in Service Builder (not yet saved as a plan)'
                                                : servicePlanResolution.origin.kind === 'saved'
                                                  ? `the saved plan \u201c${servicePlanResolution.origin.name}\u201d${servicePlanResolution.origin.savedCount > 1 ? ` (most recent of ${servicePlanResolution.origin.savedCount} saved)` : ''}`
                                                  : servicePlanResolution.origin.kind === 'restore'
                                                    ? servicePlanResolution.origin.name
                                                    : 'the service plan built in Service Builder'}
                                        </span>
                                        {servicePlanStats && servicePlanStats.serviceCount > 0 && (
                                            <>
                                                {' '}— {servicePlanStats.serviceCount}{' '}
                                                {servicePlanStats.serviceCount === 1 ? 'service' : 'services'}, about{' '}
                                                <span className="font-semibold text-slate-900">
                                                    {formatCurrency(servicePlanStats.annualValue)}/yr
                                                </span>{' '}
                                                of care
                                            </>
                                        )}
                                        .
                                    </>
                                )}
                            </p>
                            <button
                                type="button"
                                onClick={onNavigateToBuilder}
                                className="shrink-0 text-sm font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                            >
                                {resolvedServicePlan.isEstimated ? 'Build the service plan' : 'Change it in Service Builder'} ›
                            </button>
                        </div>
                        {emptySavedServicePlans.length > 0 && (
                            <p className="mt-2 text-xs text-amber-700">
                                {emptySavedServicePlans.map((p) => `“${p.name}”`).join(', ')}{' '}
                                {emptySavedServicePlans.length === 1 ? 'has' : 'have'} no services in {emptySavedServicePlans.length === 1 ? 'it' : 'them'}, so{' '}
                                {emptySavedServicePlans.length === 1 ? 'it' : 'they'} can&apos;t be priced — open the Service Builder, add services and save again.
                            </p>
                        )}
                        {savedServicePlans.length > 0 && (
                            <label className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                                Service plan:
                                {/*
                                  * SHOWS WHAT IS SELECTED, AND CAN SELECT NOTHING (owner-caught,
                                  * 2026-08-27). It was pinned to value="" — a permanent "choose…"
                                  * that never reflected the plan in use, so the control and the
                                  * sentence above it disagreed about the same fact. And having
                                  * loaded a plan there was no way back to none, which is a real
                                  * state: it is what a client sees before building anything, and
                                  * it is the "current position" the panel describes.
                                  */}
                                <select
                                    value={selectedSavedPlanId}
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            clearCurrentServicePlan();
                                            setPlanPickVersion((v) => v + 1);
                                            return;
                                        }
                                        const picked = savedServicePlans.find((p) => p.id === e.target.value);
                                        if (!picked) return;
                                        setCurrentServicePlan(picked.plan, picked.name);
                                        setPlanPickVersion((v) => v + 1);
                                    }}
                                    className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
                                >
                                    <option value="">No service plan — my current position</option>
                                    {savedServicePlans.map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                    {selectedSavedPlanId === UNSAVED_PLAN_OPTION && (
                                        /* Selections exist in the builder that were never saved. Naming
                                           them keeps the control honest rather than showing "none". */
                                        <option value={UNSAVED_PLAN_OPTION}>Services selected in Service Builder (not saved)</option>
                                    )}
                                </select>
                            </label>
                        )}
                        {/* The nudge reversed with the tab order (2026-08-27). It used to present the
                            two orders as equal, which was true of the ADVICE and not of the figures:
                            the contribution rates hold whatever services are chosen, but the dollars
                            they apply to are the illustrative package's until a real plan exists. So
                            this now says which half is provisional rather than implying neither is. */}
                        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                            You can optimise from here — the <span className="font-medium text-slate-700">contribution rates</span>{' '}
                            below are set by the means assessment and hold whatever services are chosen. The{' '}
                            <span className="font-medium text-slate-700">dollar amounts</span> are the illustrative
                            package&apos;s until a real plan is built, so build the service plan when you can and these
                            figures re-price against it.
                        </p>
                        </PlainWordsBeat>
                    </PlainWordsCard>
                )}

                {/* Strategy Mode Selection — hidden while SE analysis runs (full-screen loader, AC parity) */}
                {!sahSeAnalyzing && (
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                        <ModeChooser
                            value={internalStrategyMode}
                            onSelect={(id) => handleStrategyModeChange(id as 'manual' | 'suggestion')}
                            options={[
                                {
                                    id: 'manual',
                                    title: 'Manual Control',
                                    blurb:
                                        "Pull the levers yourself — gifting, funeral bonds, what happens with the home, an annuity, and linking a partner's aged care plan.",
                                    chip: 'Advisers & confident users',
                                },
                                {
                                    id: 'suggestion',
                                    title: 'Suggestion Engine',
                                    blurb:
                                        'It tests the sensible combinations for lowering your care contributions and lifting the Age Pension, then shows the best — ranked by your goals.',
                                    chip: 'Most people start here',
                                },
                            ]}
                        />
                    </div>
                )}

                {/* Suggestion Engine Content */}
                {internalStrategyMode === 'suggestion' && (
                    <SupportAtHomeSuggestionEnginePanel
                        onSoaGenerated={onSoaGenerated}
                        supportStartDate={supportStartInPast ? '' : supportStartDate}
                        factFind={factFind}
                        setFactFind={setFactFind}
                        // getServicePlanFromStorage's StoredServicePlan is deliberately loose (JSON.parse'd
                        // from sessionStorage); SupportAtHomeServicePlan is the strict in-memory shape the
                        // panel expects — same runtime data, structurally-incompatible declared types.
                        servicePlan={servicePlanSource as SupportAtHomeServicePlan} // The SAME source the manual mode and the case line use
                        goals={factFind.goals || {}}
                        onStrategySelect={(strategy: Record<string, unknown>) => {
                            setSahSeSelectedStrategy(strategy);
                        }}
                        selectedStrategy={sahSeSelectedStrategy}
                        legacyYears={5}
                        onNavigateToDashboard={() => onNavigateToDashboard?.()}
                        autoOpenOnMount={true}
                        selectedAgedCareIntegration={selectedAgedCareIntegration}
                        onGoToServiceBuilder={onNavigateToBuilder}
                        showBaseline={showBaseline}
                        setShowBaseline={setShowBaseline}
                        homeCareSaveContext={{
                            participantType,
                            nwopCategory,
                            meansDisclosed,
                            previousITCF,
                            isPartnered,
                        }}
                        onAnalysisStateChange={setSahSeAnalyzing}
                    />
                )}
                
                {/* Manual Control Content */}
                {internalStrategyMode === 'manual' && (
                    <div className="space-y-6">
                        {/* Integration Strategy - Partner's Aged Care Scenario - FIRST */}
                        {hasAgedCareStrategies && (
                            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-blue-600" />
                                    Partner's Aged Care Scenario
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    Link a saved aged care strategy to include the partner's accommodation deposit and ongoing aged care fees in this household projection. The figures below update all financials, contributions, and projections dynamically.
                                </p>
                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-gray-700">Select saved aged care strategy</label>
                                    <select
                                        value={selectedAgedCareStrategyId || ''}
                                        onChange={(event) => setSelectedAgedCareStrategyId(event.target.value || null)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Do not integrate an aged care scenario</option>
                                        {availableAgedCareStrategies.map((strategy) => (
                                            <option key={strategy.id} value={strategy.id}>
                                                {strategy.name} – {formatCurrency(strategy.outputs?.agedCareCost || 0)} p.a.
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {selectedAgedCareIntegration && (
                                    <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <div className="flex flex-wrap gap-6 text-sm text-blue-900">
                                            <div className="flex-1 min-w-[160px]">
                                                <p className="font-semibold text-blue-800">Annual aged care fees</p>
                                                <p className="text-xl font-bold">{formatCurrency(selectedAgedCareIntegration.annualCost)}</p>
                                            </div>
                                            <div className="flex-1 min-w-[160px]">
                                                <p className="font-semibold text-blue-800">RAD paid upfront</p>
                                                <p className="text-xl font-bold">{formatCurrency(selectedAgedCareIntegration.radPaid || 0)}</p>
                                            </div>
                                        </div>
                                        <div className="mt-4 text-xs text-blue-700 flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4" />
                                            We automatically reduce the household assets by the RAD funding and subtract the ongoing daily fees from cashflow.
                                        </div>
                                        {/* The other half of what linking does — the Age Pension reassessment
                                            (owner-caught omission, 2026-08-25): the fees and RAD were stated, the
                                            reassessment that usually dwarfs them was not. */}
                                        {integrationSummary && (
                                            <p className="mt-2 text-xs leading-relaxed text-blue-900">
                                                Linking this also changes the <strong>Age Pension assessment</strong>: with{' '}
                                                {integrationSummary.personName} in residential care
                                                {integrationSummary.illnessSeparated && (
                                                    <>
                                                        , the household is assessed as an{' '}
                                                        <JargonTerm term="illness-separated couple" /> — each paid at the
                                                        higher single rate, with more generous thresholds
                                                    </>
                                                )}
                                                {integrationSummary.radPaid > 0 && (
                                                    <>
                                                        , and the {formatCurrency(integrationSummary.radPaid)} RAD is exempt
                                                        from the pension assets test
                                                    </>
                                                )}
                                                . The Age Pension figure on the right already includes this.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Gift className="w-5 h-5 text-blue-600" />
                                Strategic Gifting
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Centrelink allows you to gift up to $10,000 per year and $30,000 over a rolling 5-year period. 
                                Gifts within these thresholds are not included in your asset test.
                            </p>
                            <div className="flex items-center gap-4">
                                <CurrencyInput
                                    label="Annual Gifting Amount:"
                                    value={manualStrategies.gifting ?? ''}
                                    onChange={(v) => setManualStrategies({...manualStrategies, gifting: v ?? 0})}
                                    className="w-40"
                                />
                            </div>
                            <GiftingFieldNote
                                giftingAmount={Number(manualStrategies.gifting) || 0}
                                giftsHistory={factFind.giftsHistory}
                            />
                            {Number(manualStrategies.gifting) > 0 && (
                                <div className="mt-4">
                                    <label className="text-sm font-medium text-gray-700">Source of funds:</label>
                                    <select
                                        value={manualStrategies.giftingSource}
                                        onChange={(e) => setManualStrategies({...manualStrategies, giftingSource: e.target.value})}
                                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="bank">From Bank Account</option>
                                        <option value="abp">From Account-Based Pension</option>
                                        <option value="shares">From Shares Portfolio</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        
                        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-blue-600" />
                                Funeral Bonds
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Funeral bonds are excluded from asset testing for both the Age Pension and Support at Home
                                assessments — but only up to {formatCurrency(FUNERAL_BOND_LIMIT)} per person (indexed each
                                year). Above that the exemption is lost, so there is no strategy benefit in going higher.
                            </p>
                            {(() => {
                                /*
                                 * Capped at the household's REMAINING exemption headroom (owner-caught,
                                 * 2026-08-25): unlike gifting — where exceeding the free areas is a
                                 * legitimate choice the engine prices as deprivation — a bond above the
                                 * limit simply isn't exempt, so the input never offers a move with no
                                 * upside. Bonds already owned use up headroom first.
                                 */
                                const existingBonds = (factFind?.assets?.funeralBonds || []).reduce(
                                    (sum: number, bond: { value?: number | string }) => sum + (parseFloat(String(bond?.value)) || 0),
                                    0,
                                );
                                const headroom = Math.max(0, FUNERAL_BOND_LIMIT * (isPartnered ? 2 : 1) - existingBonds);
                                return (
                                    <div>
                                        <div className="flex items-center gap-4">
                                            <CurrencyInput
                                                label="Additional Funeral Bond Purchase:"
                                                value={manualStrategies.funeralBonds ?? ''}
                                                onChange={(v) => setManualStrategies({ ...manualStrategies, funeralBonds: Math.min(v ?? 0, headroom) })}
                                                className="w-40"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-slate-500">
                                            Room left within the exemption: {formatCurrency(headroom)}
                                            {existingBonds > 0 && <> ({formatCurrency(existingBonds)} of bonds already held)</>}.
                                        </p>
                                    </div>
                                );
                            })()}
                            {Number(manualStrategies.funeralBonds) > 0 && (
                                <div className="mt-4">
                                    <label className="text-sm font-medium text-gray-700">Source of funds:</label>
                                    <select
                                        value={manualStrategies.funeralSource}
                                        onChange={(e) => setManualStrategies({...manualStrategies, funeralSource: e.target.value})}
                                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="bank">From Bank Account</option>
                                        <option value="abp">From Account-Based Pension</option>
                                        <option value="shares">From Shares Portfolio</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        
                        {/* Property Strategy Options - Only show if client owns their home */}
                        {anyoneOwnsHome(factFind as unknown as FactFind) && (
                            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Home className="w-5 h-5 text-blue-600" />
                                    Property Strategy Options
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    Choose how you would like to release equity from your home. Select one option below to configure the details.
                                </p>

                                <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
                                    {[
                                        {
                                            id: 'downsize',
                                            title: 'Downsize family home',
                                            description: 'Sell current home, buy lower-cost property, redirect surplus capital.'
                                        },
                                        {
                                            id: 'renovate',
                                            title: 'Renovate home',
                                            description: 'Home improvements convert assessable assets into exempt home equity.'
                                        },
                                        {
                                            id: 'upgrade',
                                            title: 'Upgrade home',
                                            description: 'Buy more expensive home to convert assessable assets into exempt home equity.'
                                        },
                                        {
                                            id: 'reverseMortgage',
                                            title: 'Reverse mortgage',
                                            description: 'Borrow against equity in home to fund needs.'
                                        },
                                        {
                                            id: 'heas',
                                            title: 'Home Equity Access Scheme',
                                            description: 'Government program providing fortnightly payments secured on your home.'
                                        }
                                    ].map(option => {
                                        const isSelected = propertyStrategy === option.id;
                                        return (
                                            <button
                                                key={option.id}
                                                type="button"
                                                onClick={() => setPropertyStrategy(option.id)}
                                                className={`text-left border rounded-lg p-4 transition-all duration-200 h-full flex flex-col ${
                                                    isSelected ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40'
                                                }`}
                                            >
                                                <div className="flex items-start gap-2 mb-4 h-[4rem]">
                                                    {option.id === 'downsize' && <Home className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    {option.id === 'renovate' && <Wrench className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    {option.id === 'upgrade' && <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    {option.id === 'reverseMortgage' && <TrendingDown className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    {option.id === 'heas' && <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    <span className="font-semibold text-gray-900 leading-tight">{option.title}</span>
                                                </div>
                                                <p className="text-xs text-gray-600 leading-relaxed flex-grow mb-3">{option.description}</p>
                                                {isSelected && (
                                                    <div className="mt-auto text-xs font-medium text-blue-700 pt-2 border-t border-blue-200">Selected</div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                {propertyStrategy !== 'none' && (
                                    <div className="mt-3 text-xs text-gray-500">
                                        Want to remove this strategy?{' '}
                                        <button
                                            type="button"
                                            onClick={() => setPropertyStrategy('none')}
                                            className="text-blue-600 underline"
                                        >
                                            Clear selection
                                        </button>
                                    </div>
                                )}

                                {propertyStrategy === 'none' && (
                                    <div className="mt-4 text-xs text-gray-500">
                                        Select a property strategy to see the detailed inputs.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Property Strategy Inputs - Show when a strategy is selected */}
                        {propertyStrategy && propertyStrategy !== 'none' && anyoneOwnsHome(factFind as unknown as FactFind) && (
                            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                                {propertyStrategy === 'downsize' && (
                                    <SupportDownsizeStrategyAllocator
                                        factFind={factFind}
                                        details={downsizeDetails}
                                        setDetails={setDownsizeDetails}
                                    />
                                )}

                                {/* Both schemes repay nothing along the way, so the number that decides
                                    the family's outcome is what is OWED at the end. The same shared
                                    notes Aged Care uses — these are means-test levers, not care ones. */}
                                {propertyStrategy === 'reverseMortgage' && (
                                    <>
                                        <ReverseMortgageStrategyAllocator
                                            factFind={factFind}
                                            details={reverseMortgageDetails}
                                            setDetails={setReverseMortgageDetails}
                                            legacyYears={legacyYears}
                                        />
                                        <ReverseMortgageFieldNote
                                            loanAmount={Number(reverseMortgageDetails?.loanAmount) || 0}
                                            interestRate={Number(reverseMortgageDetails?.interestRate) || 0}
                                            years={Number(legacyYears) || 5}
                                        />
                                    </>
                                )}

                                {propertyStrategy === 'heas' && (
                                    <>
                                        <HEASStrategyAllocator
                                            factFind={factFind}
                                            details={heasDetails}
                                            setDetails={setHeasDetails}
                                            legacyYears={legacyYears}
                                        />
                                        <HeasFieldNote
                                            paymentAmount={Number(heasDetails?.paymentAmount) || 0}
                                            paymentFrequency={String(heasDetails?.paymentFrequency || 'fortnightly')}
                                            interestRate={Number(heasDetails?.interestRate) || 0}
                                            years={Number(legacyYears) || 5}
                                        />
                                    </>
                                )}

                                {propertyStrategy === 'renovate' && (
                                    <RenovateStrategyAllocator
                                        factFind={factFind}
                                        details={renovateDetails}
                                        setDetails={setRenovateDetails}
                                        beforeFinancials={beforeFinancials ?? undefined}
                                    />
                                )}

                                {propertyStrategy === 'upgrade' && (
                                    <UpgradeStrategyAllocator
                                        factFind={factFind}
                                        details={upgradeDetails}
                                        setDetails={setUpgradeDetails}
                                        beforeFinancials={beforeFinancials ?? undefined}
                                    />
                                )}
                            </div>
                        )}
                        
                        {/* Additional Funding & Pension Strategies */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                            <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-blue-600" />
                                Additional Funding & Pension Strategies
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                                These optional strategies reshape your assets and income so you may qualify for more Age Pension support and reduce out-of-pocket care fees.
                            </p>

                            <div className="grid gap-3 md:grid-cols-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newValue = selectedFundingStrategy === 'annuity' ? null : 'annuity';
                                        setSelectedFundingStrategy(newValue);
                                        if (newValue === 'annuity' && !annuityRecord) {
                                            setAnnuityRecord(initializeAnnuityRecord());
                                        } else if (newValue === null) {
                                            setAnnuityRecord(null);
                                        }
                                    }}
                                    className={`text-left border rounded-lg p-4 transition-all duration-200 ${selectedFundingStrategy === 'annuity' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40'}`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <ShieldCheck className="w-5 h-5 text-blue-600" />
                                        <span className="font-semibold text-gray-900">Commence an annuity</span>
                                        {selectedFundingStrategy === 'annuity' && (
                                            <Badge tone="info" className="ml-auto">Selected</Badge>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-600 mb-2">
                                        Convert savings into guaranteed income. May reduce assessable assets and help cover ongoing care costs.
                                    </p>
                                    <div className="flex gap-2 flex-wrap text-[11px]">
                                        <Badge tone="info">Age Pension Focus</Badge>
                                        <Badge tone="neutral">Cashflow Stability</Badge>
                                    </div>
                                </button>
                            </div>

                            {selectedFundingStrategy === 'annuity' ? (
                                <div className="mt-4">
                                    <SupportAtHomeAnnuityConfigurator
                                        factFind={optimizedScenario?.optimizedFactFind || factFind}
                                        annuityDetails={annuityRecord}
                                        setAnnuityDetails={(updater) => setAnnuityRecord(prev => {
                                            const base: AnnuityDetails = prev ?? initializeAnnuityRecord();
                                            return typeof updater === 'function' ? (updater as (p: AnnuityDetails) => AnnuityDetails)(base) : updater;
                                        })}
                                        annuitySummary={annuitySummary}
                                        annuityWarnings={annuityWarnings}
                                        fundingSources={availableAnnuityFundingSources}
                                        formatCurrency={formatCurrency}
                                        clientAge={factFind?.client?.dob ? calculateAge(factFind.client.dob) : null}
                                        partnerAge={isPartnered && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null}
                                    />
                                </div>
                            ) : (
                                <div className="mt-4 text-xs text-gray-500">
                                    Select a strategy above to view the explainer and customise the details.
                                </div>
                            )}
                        </div>

                        {/* Shared debt strategy card — renders nothing when the client has no debt. */}
                        <DebtPayoffStrategy
                            factFind={factFind as never}
                            debtPayments={debtPayments}
                            setDebtPayments={setDebtPayments}
                            debtFunding={debtFunding}
                            setDebtFunding={setDebtFunding}
                            repaymentBasis={debtRepaymentBasis}
                            setRepaymentBasis={setDebtRepaymentBasis}
                            competingUseNote="The money comes from the same assets that fund your care and living costs at home, so clearing a debt trades the ongoing repayments for a smaller buffer."
                        />

                        {/* Save Strategy Section */}
                        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg shadow-gray-200/50 border border-white">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Save Strategy</h3>
                            <div className="space-y-4">
                                <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-4">
                                    <input
                                        type="text"
                                        placeholder="Enter strategy name (e.g., Conservative Approach)"
                                        value={optimiseStrategyName}
                                        onChange={(e) => {
                                            setOptimiseStrategyName(e.target.value);
                                            if (optimiseStrategyNameError) setOptimiseStrategyNameError('');
                                        }}
                                        className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${optimiseStrategyNameError ? 'border-red-400' : 'border-gray-300'}`}
                                        aria-invalid={Boolean(optimiseStrategyNameError)}
                                        aria-describedby={optimiseStrategyNameError ? 'optimise-strategy-name-error' : undefined}
                                    />
                                    <button
                                        onClick={() => {
                                            if (!optimiseStrategyName.trim()) {
                                                setOptimiseStrategyNameError('Please enter a strategy name');
                                                return;
                                            }
                                            setOptimiseStrategyNameError('');
                                            
                                            // Calculate annual contribution cost
                                            // Check if services are actually selected (not just using assumed distribution)
                                            const servicePlanForSave = factFind?.servicePlan as SahServicePlanShape | undefined;
                                            const hasServicePlan = Boolean(servicePlanForSave && (
                                                (servicePlanForSave.serviceDistribution &&
                                                 ((servicePlanForSave.serviceDistribution.clinical ?? 0) > 0 ||
                                                  (servicePlanForSave.serviceDistribution.independence ?? 0) > 0 ||
                                                  (servicePlanForSave.serviceDistribution.everyday ?? 0) > 0)) ||
                                                (servicePlanForSave.services && Object.keys(servicePlanForSave.services).length > 0)
                                            ));
                                            // Only save contribution if services are actually selected, otherwise save 0
                                            const annualContribution = hasServicePlan ? (afterContributionData?.contributionAmounts?.totalAnnual || 0) : 0;
                                            
                                            // Ensure contribution rates are saved - try multiple sources
                                            let contributionRatesToSave = {};
                                            
                                            // First try afterContributionData (preferred source)
                                            if (afterContributionData?.contributionRates) {
                                                contributionRatesToSave = afterContributionData.contributionRates;
                                            }
                                            // Fallback to afterContributions directly
                                            else if (isPartnered && afterContributions?.combined?.contributionRates) {
                                                contributionRatesToSave = afterContributions.combined.contributionRates;
                                            }
                                            else if (afterContributions?.client?.contributionRates) {
                                                contributionRatesToSave = afterContributions.client.contributionRates;
                                            }
                                            // Last resort: try partner if available
                                            else if (isPartnered && afterContributions?.partner?.contributionRates) {
                                                contributionRatesToSave = afterContributions.partner.contributionRates;
                                            }
                                            
                                            // Create strategy object - save ALL strategy inputs
                                            const strategy = {
                                                id: `strategy-${Date.now()}`,
                                                name: optimiseStrategyName.trim(),
                                                type: 'home_care',
                                                source: 'manual',
                                                inputs: {
                                                    source: 'manual',
                                                    // Manual strategies
                                                    gifting: manualStrategies.gifting,
                                                    funeralBonds: manualStrategies.funeralBonds,
                                                    giftingSource: manualStrategies.giftingSource,
                                                    funeralSource: manualStrategies.funeralSource,
                                                    // Property strategies
                                                    propertyStrategy: propertyStrategy,
                                                    downsizeDetails: propertyStrategy === 'downsize' ? downsizeDetails : null,
                                                    renovateDetails: propertyStrategy === 'renovate' ? renovateDetails : null,
                                                    upgradeDetails: propertyStrategy === 'upgrade' ? upgradeDetails : null,
                                                    reverseMortgageDetails: (overallStrategy.reverseMortgage || propertyStrategy === 'reverseMortgage') ? reverseMortgageDetails : null,
                                                    heasDetails: (overallStrategy.heas || propertyStrategy === 'heas') ? heasDetails : null,
                                                    // Funding strategies
                                                    selectedFundingStrategy: selectedFundingStrategy,
                                                    annuityRecord: selectedFundingStrategy === 'annuity' ? annuityRecord : null,
                                                    // The record the annuity was applied with, so the plan's position can be rebuilt (Action Plan forms).
                                                    annuityRecordForCalculation: selectedFundingStrategy === 'annuity' ? annuityRecordForCalculation : null,
                                                    // Aged care integration
                                                    agedCareContext: selectedAgedCareStrategyId ? 'linked' : 'current',
                                                    selectedAgedCareStrategyId: selectedAgedCareStrategyId || null,
                                                    // Overall strategy flags
                                                    overallStrategy: overallStrategy
                                                },
                                                outputs: {
                                                    annualContribution: annualContribution,
                                                    agePension: afterFinancials?.socialSecurity || 0,
                                                    // The panel's figure: net of the service plan's contribution where one
                                                    // exists. `cashFlowBasis` says so, so the advice document nets its own
                                                    // baseline the same way rather than comparing net with gross.
                                                    cashFlow: sahCashFlowAfter ?? (afterFinancials?.cashFlow || 0),
                                                    cashFlowBasis: sahContributionAfter != null ? 'net_of_service_plan' : 'before_care_costs',
                                                    legacyEstimate: projectedLegacy,
                                                    // The rows `projectedLegacy` reads its last entry from, kept whole so a
                                                    // client can be told what they are worth in a year that is not the last.
                                                    netWorthSchedule: buildNetWorthSchedule(projections),
                                                    financials: afterFinancials, // Include full financials for Service Builder
                                                    contributionRates: contributionRatesToSave, // Save contribution rates for comparison
                                                    /*
                                                     * WHICH SERVICE PLAN these dollars were priced against.
                                                     *
                                                     * `annualContribution` is budget x rate. The rates above survive a
                                                     * change of services; this figure does not. Without a record of the
                                                     * basis, adjusting the services afterwards left a saved strategy
                                                     * quoting a contribution for a plan that no longer exists, and
                                                     * nothing on screen could tell. See servicePlanBasis.ts.
                                                     */
                                                    servicePlanBasis: currentServicePlanBasis
                                                },
                                                createdAt: new Date().toISOString()
                                            };
                                            
                                            persistHomeCareStrategy(strategy);

                                            // Capture what the Digital SOA needs BEFORE the reset below wipes the
                                            // live modelling state — the document is generated from the saved
                                            // record plus these projections as they stood at save time.
                                            setManualSoaOffer({
                                                strategy: strategy as never,
                                                projections: ((projectionsForModal || projections) ?? null) as never,
                                            });

                                            setOptimiseStrategyName('');

                                            // Reset strategies to baseline after saving
                                            setResetTrigger(prev => prev + 1);
                                        }}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                                    >
                                        Save Strategy
                                    </button>
                                </div>
                                {optimiseStrategyNameError ? (
                                    <p id="optimise-strategy-name-error" className="text-sm text-red-600" role="alert">
                                        {optimiseStrategyNameError}
                                    </p>
                                ) : null}
                                {manualSoaOffer && (
                                    <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                                        <p className="text-sm font-semibold text-green-800 mb-1">
                                            &ldquo;{String((manualSoaOffer.strategy as { name?: string }).name || 'Strategy')}&rdquo; saved.
                                        </p>
                                        <p className="text-sm text-green-900 mb-3">
                                            Generate the Statement of Advice for it? It covers your position, the service
                                            plan modelled, each applied strategy&apos;s rationale (benefits, risks,
                                            suitability) and the year-by-year projections.
                                        </p>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => { void openManualDigitalSoa(); }}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm"
                                            >
                                                Open Digital SOA
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setManualSoaOffer(null)}
                                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm"
                                            >
                                                Maybe later
                                            </button>
                                        </div>
                                    </div>
                                )}
                                </div>

                                {/* Show saved strategies with reopen functionality */}
                                {savedHomeCareStrategies.length > 0 && (
                                    <div className="space-y-3">
                                        <div className="p-3 bg-slate-50 border-l-4 border-blue-400 rounded-r-lg">
                                            <p className="text-sm text-blue-700 mb-2">
                                                <strong>{savedHomeCareStrategies.length}</strong> home care strategies saved.
                                            </p>
                                            <div className="space-y-1">
                                                {savedHomeCareStrategies.map(strategy => (
                                                    <div key={strategy.id} className="flex items-center justify-between text-xs">
                                                        <span className="text-gray-600">
                                                            {strategy.name}
                                                            {strategy.source === 'suggestion_engine' ? ' · Suggestion engine' : ''}
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                sessionStorage.setItem('careCompanionRestoreHomeCareStrategy', JSON.stringify(strategy));
                                                                window.location.reload();
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline font-semibold"
                                                        >
                                                            Reopen
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        {savedHomeCareStrategies.length > 1 && (
                                            <button
                                                onClick={() => setShowCompareModal(true)}
                                                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                                            >
                                                Compare Strategies ({savedHomeCareStrategies.length})
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                </div>
            
            {/* Right Column - Current Contribution Rates & Financial Impact */}
            <div className={`xl:col-span-2 space-y-6 xl:sticky xl:top-8 xl:self-start transition-all duration-300 ${rightColumnBlurred ? 'blur-sm pointer-events-none' : ''}`}>
                {/*
                  * ONE PANEL, the same artifact Aged Care uses (owner, 2026-08-27: "I want the
                  * UI/UX experience consistent across the modules").
                  *
                  * This rail held two cards that had drifted apart from the rest of the app: a
                  * blue/teal gradient block of three large contribution tiles, and — the bigger
                  * problem — a HAND-ROLLED copy of the Financial Impact panel, with its own heading,
                  * its own legacy-years control and its own tile grid. A copy cannot inherit a fix:
                  * the compact-row layout, the embedded chrome rules and the tile alignment work all
                  * landed in FinancialImpactPanel and never reached here.
                  *
                  * So the copy is gone and the shared component is used, inside the shared
                  * PlanSummaryPanel. The contribution RATES take the "what care costs" slot, which
                  * is what they are in this module — Support at Home charges a share of each service
                  * rather than a daily fee, so the rates are the price.
                  */}
                <PlanSummaryPanel
                    strategyName={sahPlanSummaryName}
                    baselineLabel={sahDoNothingLegacy != null ? 'no plan' : undefined}
                    inBrief={sahInBrief}
                    onReadFullExplanation={sahPlanSummaryName ? () => setShowSahExplanation(true) : undefined}
                    onViewProjections={() => setShowProjectionsModal(true)}
                    /*
                     * SAY WHAT THIS IS (owner-caught, 2026-08-27). Without a service plan these are
                     * not a baseline — they are the client's position today, and the illustrative
                     * package is standing in for services nobody has chosen. Calling that "the
                     * baseline" pointed the reader at a placeholder as the thing to beat.
                     *
                     * With a plan built, the no-strategy state IS the benchmark, exactly as it is in
                     * Aged Care, so the default wording returns.
                     */
                    noStrategyHeading={
                        resolvedServicePlan.isEstimated ? 'No service plan yet — your position today' : 'No plan yet — the starting position'
                    }
                    noStrategyNote={
                        resolvedServicePlan.isEstimated ? (
                            <>
                                This is your <span className="font-semibold text-slate-700">current position</span>.
                                Build the service plan and it becomes the plan every strategy here is measured against.
                            </>
                        ) : undefined
                    }
                    costOverview={panelContributionData ? (
                        <div className="bg-gray-800 text-white rounded-xl p-4">
                            {/*
                              * THE RATES ARE REAL; THE DOLLARS ARE NOT — until a service plan exists.
                              *
                              * Out-of-pocket is budget x rate. The rates come from the means
                              * assessment, hold whatever services are chosen, and are the thing
                              * Optimise actually moves — so they are shown, and they are true now.
                              * The budget does not exist until a plan is built, so quoting a dollar
                              * share would be the illustrative package's figure wearing the client's
                              * name: the same fault as the Suggestion Engine inventing a room price.
                              * Blanking the whole tile would throw away the half that IS true.
                              */}
                            <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-white/15">
                                <p className="text-gray-400 text-xs leading-snug">Your share<br />of care costs</p>
                                <div className="text-right">
                                    {resolvedServicePlan.isEstimated ? (
                                        <>
                                            <p className="text-lg font-bold text-white leading-tight">Needs a service plan</p>
                                            <button
                                                type="button"
                                                onClick={() => onNavigateToBuilder?.()}
                                                className="text-xs font-semibold text-blue-300 underline underline-offset-2 hover:text-blue-200"
                                            >
                                                Build it in Service Builder ›
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-3xl font-bold text-white leading-none">
                                                {formatCurrency(panelContributionData.contributionAmounts?.totalAnnual || 0)}
                                            </p>
                                            <p className="text-gray-300 font-semibold text-xs mt-1">a year</p>
                                        </>
                                    )}
                                </div>
                            </div>
                            {/* The three rates as rows, mirroring Aged Care's fee rows — the same
                                shape, so a client who has seen one recognises the other. "was"
                                sits under the rate it replaced rather than in a separate tile. */}
                            <div className="space-y-2.5">
                                {([
                                    ['Clinical Care', 'clinical'],
                                    ['Independence', 'independence'],
                                    ['Everyday Living', 'everydayLiving'],
                                ] as const).map(([label, key]) => {
                                    const now = panelContributionData.contributionRates?.[key] || 0;
                                    const was = beforeContributionData?.contributionRates?.[key];
                                    const moved = showContributionWas && was != null && was !== now;
                                    return (
                                        <div key={key} className="flex items-baseline justify-between gap-3 text-sm">
                                            <span className="text-gray-300">{label}</span>
                                            <span className="text-right">
                                                <span className="font-semibold text-white tabular-nums">{formatPercent(now)}</span>
                                                {moved && (
                                                    <span className="block text-[11px] text-gray-400 tabular-nums">
                                                        was {formatPercent(was)}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : <div />}
                    financialImpact={panelFinancials ? (
                        <FinancialImpactPanel
                            embedded
                            before={beforeFinancials as never}
                            after={impactAfterFinancials as never}
                            legacyYears={legacyYears}
                            setLegacyYears={setLegacyYears}
                            onViewProjections={() => setShowProjectionsModal(true)}
                            netWorthAfterValue={impactAfterFinancials?.netWorth ?? null}
                            cashFlowBeforeValue={sahCashFlowBefore}
                            cashFlowAfterValue={sahCashFlowAfter}
                            cashFlowBreakdownOverride={sahCashFlowBreakdown}
                            /* Measured against RECEIVING THE SERVICES WITH NO STRATEGY, projected
                               over the same years. Support at Home is a care requirement, not an
                               elective move, so the alternative is not "don't do it" — it is doing
                               it without a financial plan. Comparing with net worth TODAY credited
                               every strategy with growth that happens either way. */
                            legacyBaselineValue={sahDoNothingLegacy}
                            legacyEstimateOverride={impactProjectedLegacy}
                            baselineLabel={sahDoNothingLegacy != null ? 'no plan' : undefined}
                        />
                    ) : <div />}
                />

                {/* THE aged care design, not a copy of it: the shared engine view with charts, the
                    monthly narrative and per-figure tooltips. The old module-specific modal showed the
                    table alone — "matching" the aged care design in layout while missing everything
                    that made it explainable. */}
                {/* "Understanding your plan" — the recap on its own surface, so its reasons can
                    simply be read rather than tapped for one at a time. Same modal and same
                    component as Aged Care and Retirement Village. */}
                {showSahExplanation && (
                    <PlanExplanationModal
                        strategyName={sahPlanSummaryName}
                        onClose={() => setShowSahExplanation(false)}
                    >
                        {sahRecapNode}
                    </PlanExplanationModal>
                )}
                {/* The FY blend on row 1 is a FINANCIAL-year device, so calendar mode shows the
                    calendar run's own rows — the choice the Aged Care analyser makes. */}
                {showProjectionsModal && projections && (
                    <ProjectionsModal
                        onClose={() => setShowProjectionsModal(false)}
                        projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : (projectionsForModal || projections)) as never}
                        onPeriodModeChange={setModalPeriodMode}
                        isReloading={modalPeriodMode === 'calendar_year' && !calendarYearProjections}
                        factFind={factFind as never}
                        initialFinancials={beforeFinancials as never}
                        currentFinancials={afterFinancials as never}
                        agedCareCosts={(combinedCareCosts || null) as never}
                        costLabel="Support at Home Costs"
                    />
                )}
            </div>
        </div>
        
        {/* Compare Strategies Modal */}
        {showCompareModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80">
                    <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                                    <GradientText>Compare Home Care Strategies</GradientText>
                                </h2>
                                <p className="text-sm text-slate-600 mt-1.5">Side-by-side view of saved Support at Home strategies (model outputs).</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowCompareModal(false)}
                                className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors text-2xl leading-none"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="h-1 w-full rounded-full bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 mt-4 opacity-90" aria-hidden />
                    </div>

                    <div className="overflow-auto flex-1 px-6 py-5">
                        <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                            <table className="w-full text-sm border-collapse min-w-[720px]">
                                <thead>
                                    <tr className="bg-slate-100 text-slate-800">
                                        <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Strategy name</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Age pension</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Cash flow</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Legacy estimate</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Annual contribution</th>
                                        <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Clinical care</th>
                                        <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Independence</th>
                                        <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Everyday living</th>
                                        <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {savedHomeCareStrategies.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                                                        No strategies saved yet. Save a strategy to compare.
                                                    </td>
                                                </tr>
                                            ) : (
                                            savedHomeCareStrategies.map((strategy, index) => {
                                                const contributionRates = (strategy.outputs?.contributionRates || {}) as SahContributionRateSet;
                                                const formatPercentModal = (value: number | undefined) => {
                                                    if (!value && value !== 0) return 'N/A';
                                                    return `${(value * 100).toFixed(1)}%`;
                                                };
                                                const cf = Number(strategy.outputs?.cashFlow) || 0;
                                                return (
                                                    <tr
                                                        key={strategy.id}
                                                        className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                                                    >
                                                        <td className="px-4 py-3 font-medium text-slate-900 align-top">
                                                            {strategy.name}
                                                            {strategy.source === 'suggestion_engine' ? (
                                                                <span className="block text-xs font-normal text-slate-500">Suggestion engine</span>
                                                            ) : null}
                                                            {/* The contribution column is budget x rate. Change the services
                                                                afterwards and the rate still holds while the dollars do not,
                                                                so a strategy priced on a different plan says so HERE — beside
                                                                its own name, where the figures are being compared. */}
                                                            {(() => {
                                                                const warning = servicePlanBasisWarning(
                                                                    strategy.outputs?.servicePlanBasis as ServicePlanBasis | undefined,
                                                                    currentServicePlanBasis,
                                                                );
                                                                return warning ? (
                                                                    <span className="mt-1 block rounded border border-slate-200 border-l-4 border-l-amber-400 bg-white px-2 py-1 text-[11px] font-normal leading-snug text-slate-700">
                                                                        {warning}
                                                                    </span>
                                                                ) : null;
                                                            })()}
                                                        </td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(Number(strategy.outputs?.agePension) || 0)}</td>
                                                        <td className={`px-4 py-3 text-right tabular-nums font-medium ${cf < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{formatCurrency(cf)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(Number(strategy.outputs?.legacyEstimate) || 0)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(strategy.outputs?.annualContribution || 0)}</td>
                                                        <td className="px-4 py-3 text-center">{formatPercentModal(contributionRates.clinical)}</td>
                                                        <td className="px-4 py-3 text-center">{formatPercentModal(contributionRates.independence)}</td>
                                                        <td className="px-4 py-3 text-center">{formatPercentModal(contributionRates.everydayLiving)}</td>
                                                        <td className="px-4 py-3 text-center text-slate-600 align-top">
                                                            {strategy.createdAt ? new Date(String(strategy.createdAt)).toLocaleDateString() : ''}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                            )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="text-xs text-slate-500">Select a strategy in Optimise Your Plan to view the explainer and customise details.</p>
                        <Button
                            type="button"
                            onClick={() => setShowCompareModal(false)}
                            variant="secondary"
                            className="shrink-0 self-end sm:self-auto"
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};
export default StrategiesSection;